import { useCallback, useEffect, useState } from "react";
import { Link, Navigate, useParams } from "react-router-dom";
import { BookOpen, FileSpreadsheet, Plus, Trash2, Loader2, Wallet, Landmark, TrendingUp, TrendingDown, Boxes, SlidersHorizontal, Pencil, RotateCcw, Users2, MessageCircle, Printer, ArrowUpRight } from "lucide-react";
import { toast } from "sonner";
import { API, inr, downloadExcel } from "@/api";
import { EmptyState } from "@/components/EmptyState";
import Documents from "@/pages/Documents";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";

const today = () => new Date().toLocaleDateString("en-CA");
const emptyExpense = { date: today(), category: "", amount: "", account_id: "", note: "" };
const emptyPayment = { date: today(), type: "receipt", party: "", amount: "", account_id: "", note: "", ref: "", image_url: "", invoice_id: "", purchase_id: "", customer_id: "", auto_link: false };
const emptyAccount = { name: "", type: "cash", opening_balance: 0 };
const emptyPurchase = { vendor: "", date: today(), note: "", paid_amount: "", account_id: "", items: [{ product_id: "", name: "", qty: 1, rate: 0 }] };
const emptyParty = { name: "", type: "vendor", phone: "", opening_balance: 0, note: "" };

const SECTIONS = {
  sales: [
    { view: "performa", label: "Performa Invoice" },
    { view: "invoices", label: "Sale Invoice" },
    { view: "payment-in", label: "Payment-In" },
    { view: "sale-return", label: "Sale Return" },
  ],
  stock: [
    { view: "purchases", label: "Purchase" },
    { view: "stock", label: "Stock" },
    { view: "payment-out", label: "Payment-Out" },
  ],
  accounts: [
    { view: "expenses", label: "Expenses" },
    { view: "parties", label: "Parties" },
    { view: "cash", label: "Cash In Hand" },
  ],
  reports: [
    { view: "pnl", label: "P&L" },
    { view: "balance-sheet", label: "Balance Sheet" },
    { view: "profit", label: "Profit (Item/Client)" },
    { view: "sale-report", label: "Sale Report" },
    { view: "all-transactions", label: "All Transactions" },
    { view: "daybook", label: "Daybook Report" },
    { view: "party-statement", label: "Party Statement" },
    { view: "cash-flow", label: "Cash Flow" },
  ],
};

const TITLES = {
  "sales/performa": ["Performa Invoices", "Quotations shared with B2B customers before sale"],
  "sales/invoices": ["Sale Invoices", "B2B sales register with payment tracking"],
  "sales/payment-in": ["Payment-In", "Receipts from customers"],
  "sales/sale-return": ["Sale Returns", "Goods returned by customers — stock goes back to the B2B warehouse"],
  "stock/purchases": ["Purchases", "Bills from challans (Company 1) and direct vendors"],
  "stock/stock": ["B2B Stock", "Current stock in the B2B warehouse at challan purchase rates"],
  "stock/payment-out": ["Payment-Out", "Payments to vendors incl. Company 1"],
  "accounts/expenses": ["Expenses", "Category-wise business expenses"],
  "accounts/parties": ["Parties", "Customers, vendors and Company 1 — who owes whom"],
  "accounts/cash": ["Cash In Hand", "Cash & bank accounts with full ledgers"],
  "reports/pnl": ["Profit & Loss", "Company 2 (B2B) — sales, COGS, expenses"],
  "reports/balance-sheet": ["Balance Sheet", "Company 2 (B2B) financial position"],
  "reports/profit": ["Profit — Item & Client wise", "Margin after challan purchase cost"],
  "reports/sale-report": ["Sale Report", "All sale invoices with payment status"],
  "reports/all-transactions": ["All Transactions", "One combined ledger across every module"],
  "reports/daybook": ["Daybook Report", "Day-wise summary of all money movement"],
  "reports/party-statement": ["Party Statement", "Printable transaction statement per party"],
  "reports/cash-flow": ["Cash Flow", "Opening → inflows/outflows → closing, day-wise"],
};

function Money({ v, className = "" }) {
  const neg = Number(v) < 0;
  return <span className={`${neg ? "text-rose-600" : ""} ${className}`}>{inr(v)}</span>;
}

export default function Accounting() {
  const { section, view } = useParams();
  const key = `${section || ""}/${view || ""}`;
  const [range, setRange] = useState({ start: "", end: "" });
  const [pnl, setPnl] = useState(null);
  const [bs, setBs] = useState(null);
  const [profit, setProfit] = useState({ customer_wise: [], item_wise: [] });
  const [salesRows, setSalesRows] = useState([]);
  const [payments, setPayments] = useState([]);
  const [saleReturns, setSaleReturns] = useState([]);
  const [purchases, setPurchases] = useState([]);
  const [purchaseReturns, setPurchaseReturns] = useState([]);
  const [stock, setStock] = useState([]);
  const [expenses, setExpenses] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [selAccount, setSelAccount] = useState(null);
  const [accTxns, setAccTxns] = useState([]);
  const [parties, setParties] = useState([]);
  const [selParty, setSelParty] = useState(null);
  const [ledger, setLedger] = useState([]);
  const [products, setProducts] = useState([]);
  const [dlg, setDlg] = useState("");
  const [expForm, setExpForm] = useState(emptyExpense);
  const [editExpId, setEditExpId] = useState(null);
  const [expCatMode, setExpCatMode] = useState("existing");
  const [inlineCatName, setInlineCatName] = useState("");
  const [payForm, setPayForm] = useState(emptyPayment);
  const [linkSearch, setLinkSearch] = useState("");
  const [payPartyQuery, setPayPartyQuery] = useState("");
  const [importText, setImportText] = useState("");
  const [importing, setImporting] = useState(false);
  const [ledgerQuery, setLedgerQuery] = useState("");
  const [purStatusFilter, setPurStatusFilter] = useState("all");
  const [expView, setExpView] = useState("items");
  const [expCategories, setExpCategories] = useState([]);
  const [expCatSummary, setExpCatSummary] = useState([]);
  const [newCatName, setNewCatName] = useState("");
  const [newCatKind, setNewCatKind] = useState("indirect");
  const [cashTab, setCashTab] = useState("bank");
  const [transferForm, setTransferForm] = useState({ from_account_id: "", to_account_id: "", amount: "", date: today(), note: "" });
  const [cashMoveForm, setCashMoveForm] = useState({ kind: "deposit", amount: "", date: today(), note: "" });
  const [daybookDate, setDaybookDate] = useState(today());
  const [daybook, setDaybook] = useState(null);
  const [cashFlow, setCashFlow] = useState(null);
  const [allTxns, setAllTxns] = useState([]);
  const [stmtParty, setStmtParty] = useState("");
  const [tblSort, setTblSort] = useState({});
  const toggleTblSort = (t, k) => setTblSort((s) => ({ ...s, [t]: { key: k, dir: s[t]?.key === k && s[t].dir === "asc" ? "desc" : "asc" } }));
  const applySortTo = (t, rows) => {
    const s = tblSort[t];
    if (!s) return rows;
    return [...rows].sort((x, y) => {
      const va = x[s.key] ?? "";
      const vb = y[s.key] ?? "";
      const cmp = typeof va === "number" && typeof vb === "number" ? va - vb : String(va).localeCompare(String(vb));
      return s.dir === "asc" ? cmp : -cmp;
    });
  };
  const SortTh = ({ t, k, label, className = "", testid }) => (
    <TableHead className={`cursor-pointer select-none hover:text-slate-900 ${className}`} onClick={() => toggleTblSort(t, k)} data-testid={testid}>
      <span className="inline-flex items-center gap-1">{label}{tblSort[t]?.key === k ? (tblSort[t].dir === "asc" ? " ↑" : " ↓") : ""}</span>
    </TableHead>
  );
  const [accForm, setAccForm] = useState(emptyAccount);
  const [adjForm, setAdjForm] = useState({ balance: "", note: "" });
  const [srForm, setSrForm] = useState({ invoice_id: "", reason: "", qtys: {} });
  const [srItems, setSrItems] = useState([]);
  const [purForm, setPurForm] = useState(emptyPurchase);
  const [editPurId, setEditPurId] = useState(null);
  const [prForm, setPrForm] = useState({ purchase: null, qtys: {}, reason: "" });
  const [partyForm, setPartyForm] = useState(emptyParty);
  const [editPartyId, setEditPartyId] = useState(null);
  const [editPayId, setEditPayId] = useState(null);
  const [partySummary, setPartySummary] = useState(null);
  const [saving, setSaving] = useState(false);

  const params = { start: range.start, end: range.end };

  const load = useCallback(async () => {
    try {
      if (key === "reports/pnl") setPnl((await API.get("/accounting/pnl", { params })).data);
      else if (key === "reports/balance-sheet") setBs((await API.get("/accounting/reports/balance-sheet")).data);
      else if (key === "reports/profit") setProfit((await API.get("/accounting/profit", { params })).data);
      else if (key === "sales/payment-in" || key === "stock/payment-out") {
        const [pa, ac, sr, pu] = await Promise.all([API.get("/accounting/payments"), API.get("/accounting/accounts"), API.get("/accounting/sales-register"), API.get("/accounting/purchases")]);
        setPayments(pa.data); setAccounts(ac.data); setSalesRows(sr.data); setPurchases(pu.data);
      } else if (key === "sales/sale-return") {
        const [sr, sl] = await Promise.all([API.get("/accounting/sale-returns"), API.get("/accounting/sales-register")]);
        setSaleReturns(sr.data); setSalesRows(sl.data);
      } else if (key === "stock/purchases") {
        const [pu, pr, pr2, ac] = await Promise.all([API.get("/accounting/purchases"), API.get("/accounting/purchase-returns"), API.get("/inventory/stock"), API.get("/accounting/accounts")]);
        setPurchases(pu.data); setPurchaseReturns(pr.data); setProducts(pr2.data); setAccounts(ac.data);
      } else if (key === "stock/stock") setStock((await API.get("/accounting/b2b-stock")).data);
      else if (key === "accounts/expenses") {
        const [ex, ac, cs, ec] = await Promise.all([API.get("/accounting/expenses"), API.get("/accounting/accounts"), API.get("/accounting/expense-categories/summary"), API.get("/accounting/expense-categories")]);
        setExpenses(ex.data); setAccounts(ac.data); setExpCatSummary(cs.data); setExpCategories(ec.data);
      } else if (key === "accounts/parties") setParties((await API.get("/accounting/parties")).data);
      else if (key === "accounts/cash") setAccounts((await API.get("/accounting/accounts")).data);
      else if (key === "reports/daybook") setDaybook((await API.get("/accounting/reports/daybook", { params: { date: daybookDate } })).data);
      else if (key === "reports/cash-flow") setCashFlow((await API.get("/accounting/reports/cash-flow", { params })).data);
      else if (key === "reports/all-transactions") setAllTxns((await API.get("/accounting/reports/all-transactions", { params })).data);
      else if (key === "reports/sale-report") setSalesRows((await API.get("/accounting/sales-register", { params })).data);
      else if (key === "reports/party-statement") { setParties((await API.get("/accounting/parties")).data); if (stmtParty) await loadLedger(stmtParty); }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load");
    }
  }, [key, range.start, range.end]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (dlg === "payment" && !parties.length) API.get("/accounting/parties").then((r) => setParties(r.data)).catch(() => {});
  }, [dlg]);

  if (!section || !SECTIONS[section] || (view && !SECTIONS[section].some((v) => v.view === view))) {
    return <Navigate to="/accounts/reports/pnl" replace />;
  }
  if (!view) return <Navigate to={`/accounts/${section}/${SECTIONS[section][0].view}`} replace />;

  const loadAccTxns = async (a) => {
    setSelAccount(a);
    try {
      const { data } = await API.get(`/accounting/accounts/${a.id}/transactions`);
      setAccTxns(data.transactions);
    } catch {
      setAccTxns([]);
    }
  };

  const loadLedger = async (name) => {
    setSelParty(name);
    try {
      const { data } = await API.get("/accounting/parties/ledger", { params: { name } });
      setLedger(data.transactions);
      setPartySummary(data.party || null);
    } catch {
      setLedger([]);
      setPartySummary(null);
    }
  };

  const openEditPayment = (p) => {
    setEditPayId(p.id);
    setPayForm({ date: p.date || today(), type: p.type, party: p.party || "", amount: p.amount, account_id: p.account_id || "", note: p.note || "", ref: p.ref || "", image_url: p.image_url || "", invoice_id: p.invoice_id || "", purchase_id: p.purchase_id || "", customer_id: p.customer_id || "", auto_link: false });
    setPayPartyQuery("");
    setLinkSearch("");
    setDlg("payment");
  };

  const partyPayment = (p) => {
    const type = p.balance > 0 ? "receipt" : "payment";
    setPayForm({ ...emptyPayment, type, party: p.name, amount: Math.abs(p.balance), account_id: accounts[0]?.id || "" });
    if (!accounts.length) API.get("/accounting/accounts").then((r) => setAccounts(r.data)).catch(() => {});
    setDlg("payment");
  };

  const submit = async (kind) => {
    if (saving) return;
    setSaving(true);
    try {
      if (kind === "expense") {
        const category = (expCatMode === "new" ? inlineCatName : expForm.category).trim();
        if (!category || !(Number(expForm.amount) > 0)) { setSaving(false); return toast.error("Category and amount are required"); }
        if (expCatMode === "new") await API.post("/accounting/expense-categories", { name: category, kind: "indirect" });
        const payload = { ...expForm, category, amount: Number(expForm.amount) };
        if (editExpId) await API.patch(`/accounting/expenses/${editExpId}`, payload);
        else await API.post("/accounting/expenses", payload);
        setExpForm(emptyExpense); setEditExpId(null); setInlineCatName(""); setExpCatMode("existing");
      } else if (kind === "payment") {
        if (!payForm.party.trim() || !(Number(payForm.amount) > 0) || !payForm.account_id) { setSaving(false); return toast.error("Party, amount and account are required"); }
        if (editPayId) {
          await API.patch(`/accounting/payments/${editPayId}`, { ...payForm, amount: Number(payForm.amount) });
          toast.success("Payment updated — dues, party balance and account balance recomputed");
        } else if (payForm.auto_link) {
          const { data } = await API.post("/accounting/payments/auto-link", {
            date: payForm.date,
            type: payForm.type,
            party: payForm.party,
            amount: Number(payForm.amount),
            account_id: payForm.account_id,
            note: payForm.note,
            image_url: payForm.image_url,
          });
          const linked = data.allocations.filter((p) => p.invoice_id || p.purchase_id).length;
          toast.success(data.unallocated > 0 ? `Auto-linked to ${linked} bill(s); ${inr(data.unallocated)} left unallocated` : `Auto-linked across ${linked} bill(s)`);
        } else {
          await API.post("/accounting/payments", { ...payForm, amount: Number(payForm.amount) });
          toast.success(payForm.invoice_id ? "Receipt saved — invoice marked paid if fully covered" : "Saved");
        }
        setPayForm(emptyPayment);
        setEditPayId(null);
        setPayPartyQuery(""); setLinkSearch("");
        setDlg(""); load();
        if (selParty) loadLedger(selParty);
        setSaving(false);
        return;
      } else if (kind === "account") {
        if (!accForm.name.trim()) { setSaving(false); return toast.error("Account name is required"); }
        await API.post("/accounting/accounts", { ...accForm, opening_balance: Number(accForm.opening_balance) || 0 });
        setAccForm(emptyAccount);
      } else if (kind === "sale-return") {
        const items = Object.entries(srForm.qtys).filter(([, q]) => Number(q) > 0).map(([pid, q]) => ({ product_id: pid, qty: Number(q) }));
        if (!srForm.invoice_id || !items.length) { setSaving(false); return toast.error("Pick an invoice and return qty"); }
        await API.post("/accounting/sale-returns", { invoice_id: srForm.invoice_id, reason: srForm.reason, items });
        toast.success("Sale return saved — stock added back to B2B warehouse");
        setSrForm({ invoice_id: "", reason: "", qtys: {} });
        setSrItems([]);
      } else if (kind === "purchase") {
        const items = purForm.items.filter((i) => i.name.trim() && Number(i.qty) > 0);
        if (!purForm.vendor.trim() || !items.length) { setSaving(false); return toast.error("Vendor and at least one item required"); }
        const payload = { ...purForm, paid_amount: Number(purForm.paid_amount) || 0, items: items.map((i) => ({ ...i, qty: Number(i.qty), rate: Number(i.rate) || 0 })) };
        if (editPurId) {
          await API.patch(`/accounting/purchases/${editPurId}`, payload);
          toast.success("Purchase updated — stock adjusted");
        } else {
          await API.post("/accounting/purchases", payload);
          toast.success("Purchase bill created — stock added to B2B warehouse");
        }
        setPurForm(emptyPurchase); setEditPurId(null);
      } else if (kind === "purchase-return") {
        const items = Object.entries(prForm.qtys).filter(([, q]) => Number(q) > 0).map(([pid, q]) => ({ product_id: pid, qty: Number(q) }));
        if (!prForm.purchase || !items.length) { setSaving(false); return toast.error("Enter return qty"); }
        await API.post("/accounting/purchase-returns", { purchase_id: prForm.purchase.id, reason: prForm.reason, items });
        toast.success("Purchase return saved — stock moved out");
        setPrForm({ purchase: null, qtys: {}, reason: "" });
      } else if (kind === "party") {
        if (!partyForm.name.trim()) { setSaving(false); return toast.error("Party name is required"); }
        if (editPartyId) await API.patch(`/accounting/parties/${editPartyId}`, { ...partyForm, opening_balance: Number(partyForm.opening_balance) || 0 });
        else await API.post("/accounting/parties", { ...partyForm, opening_balance: Number(partyForm.opening_balance) || 0 });
        setPartyForm(emptyParty); setEditPartyId(null);
      }
      toast.success(kind === "payment" ? "" : "Saved");
      setDlg("");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (kind, id) => {
    try {
      await API.delete(`/accounting/${kind}/${id}`);
      toast.success("Deleted");
      if (selAccount) loadAccTxns(selAccount);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Delete failed");
    }
  };

  const pickSrInvoice = async (id) => {
    setSrForm({ ...srForm, invoice_id: id, qtys: {} });
    if (!id) { setSrItems([]); return; }
    try {
      const { data } = await API.get(`/documents/${id}`);
      setSrItems((data.items || []).filter((i) => i.product_id));
    } catch {
      setSrItems([]);
    }
  };

  const openEditPurchase = (p) => {
    setEditPurId(p.id);
    setPurForm({ vendor: p.vendor, date: p.date || today(), note: p.note || "", paid_amount: "", account_id: "", items: p.items.map((i) => ({ product_id: i.product_id || "", name: i.name, qty: i.qty, rate: i.rate })) });
    setDlg("purchase");
  };

  const xl = (kind) => downloadExcel(`/accounting/export/${kind}?start=${range.start}&end=${range.end}`, `${kind}.xlsx`);
  const ExportBtn = ({ kind, testid }) => (
    <Button variant="outline" size="sm" onClick={() => xl(kind)} data-testid={testid}>
      <FileSpreadsheet className="mr-1 h-3.5 w-3.5" /> Excel
    </Button>
  );
  const accountSelect = (value, onChange, testid) => (
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger className="mt-1.5" data-testid={testid}><SelectValue placeholder="Select account" /></SelectTrigger>
      <SelectContent>
        {accounts.map((a) => <SelectItem key={a.id} value={a.id}>{a.name} ({a.type})</SelectItem>)}
      </SelectContent>
    </Select>
  );

  const [title, subtitle] = TITLES[key] || ["Accounts", ""];
  const paymentRows = payments.filter((p) => (key === "sales/payment-in" ? p.type === "receipt" : p.type === "payment"));
  const catSummary = Object.entries(expenses.reduce((m, e) => { m[e.category] = (m[e.category] || 0) + e.amount; return m; }, {})).sort((a, b) => b[1] - a[1]);
  const expenseCategoryNames = [...new Set([
    ...expCategories.map((c) => c.name),
    ...expCatSummary.map((c) => c.name),
    ...expenses.map((e) => e.category),
  ].filter(Boolean))].sort((a, b) => a.localeCompare(b));
  const openExpenseDialog = (expense = null) => {
    const category = expense?.category || "";
    const custom = category && !expenseCategoryNames.includes(category);
    setEditExpId(expense?.id || null);
    setExpCatMode(custom || (!category && expenseCategoryNames.length === 0) ? "new" : "existing");
    setInlineCatName(custom ? category : "");
    setExpForm(expense ? {
      date: expense.date || today(),
      category: custom ? "" : category,
      amount: expense.amount,
      account_id: expense.account_id || "",
      note: expense.note || "",
    } : { ...emptyExpense, account_id: accounts[0]?.id || "" });
    setDlg("expense");
  };

  return (
    <div className="space-y-6" data-testid="accounting-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-heading text-3xl font-bold text-slate-900">{title}</h1>
          <p className="text-sm text-slate-500">{subtitle}</p>
        </div>
        <div className="flex flex-wrap items-center gap-2" data-testid="accounting-range">
          <Input type="date" value={range.start} onChange={(e) => setRange({ ...range, start: e.target.value })} className="w-40" data-testid="range-start" />
          <span className="text-xs text-slate-400">to</span>
          <Input type="date" value={range.end} onChange={(e) => setRange({ ...range, end: e.target.value })} className="w-40" data-testid="range-end" />
          {range.start || range.end ? <Button variant="ghost" size="sm" onClick={() => setRange({ start: "", end: "" })} data-testid="range-clear">Clear</Button> : null}
        </div>
      </div>

      <div className="flex flex-wrap gap-1.5" data-testid="accounts-subnav">
        {SECTIONS[section].map((v) => (
          <Link
            key={v.view}
            to={`/accounts/${section}/${v.view}`}
            data-testid={`subnav-${section}-${v.view}`}
            className={`rounded-full px-3.5 py-1.5 text-[13px] font-medium transition-colors duration-150 ${view === v.view ? "bg-indigo-600 text-white" : "bg-white text-slate-600 ring-1 ring-slate-200 hover:bg-slate-50"}`}
          >
            {v.label}
          </Link>
        ))}
      </div>

      {key === "reports/pnl" && pnl && (
        <div data-testid="pnl-cards">
          <div className="grid grid-cols-2 gap-4 md:grid-cols-3 xl:grid-cols-6">
            {[
              { label: "Sales", value: pnl.sales, icon: TrendingUp, tone: "text-emerald-600" },
              { label: "Purchases", value: pnl.purchases, icon: BookOpen, tone: "text-slate-700" },
              { label: "COGS", value: pnl.cogs, icon: Boxes, tone: "text-amber-600" },
              { label: "Gross Profit", value: pnl.gross_profit, icon: TrendingUp, tone: pnl.gross_profit >= 0 ? "text-emerald-600" : "text-rose-600" },
              { label: "Expenses", value: pnl.expenses, icon: TrendingDown, tone: "text-rose-600" },
              { label: "Net Profit", value: pnl.net_profit, icon: Wallet, tone: pnl.net_profit >= 0 ? "text-emerald-700" : "text-rose-700" },
            ].map((c) => (
              <Card key={c.label} className="border-slate-200">
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div className="text-xs uppercase text-slate-500">{c.label}</div>
                    <c.icon className={`h-4 w-4 ${c.tone}`} />
                  </div>
                  <div className={`font-heading text-xl font-bold ${c.tone}`} data-testid={`pnl-${c.label.toLowerCase()}`}><Money v={c.value} /></div>
                </CardContent>
              </Card>
            ))}
          </div>
          <p className="mt-3 text-xs text-slate-400">{pnl.invoice_count} invoices in period · sale returns −{inr(pnl.sale_returns || 0)} · purchase returns −{inr(pnl.purchase_returns || 0)} · COGS at weighted-average purchase rates</p>
        </div>
      )}

      {key === "reports/balance-sheet" && bs && (
        <div className="grid grid-cols-1 gap-6 lg:grid-cols-2" data-testid="balance-sheet">
          <Card className="border-slate-200">
            <CardContent className="p-5">
              <div className="mb-3 text-sm font-semibold uppercase tracking-wide text-emerald-700">Assets</div>
              {[["Cash & Bank", bs.assets.cash_bank], ["B2B Stock Value", bs.assets.stock_value], ["Receivables (You'll Get)", bs.assets.receivables]].map(([l, v]) => (
                <div key={l} className="flex justify-between border-b border-slate-100 py-2.5 text-sm"><span className="text-slate-600">{l}</span><b>{inr(v)}</b></div>
              ))}
              <div className="flex justify-between py-3 text-sm font-bold text-emerald-700" data-testid="bs-assets-total"><span>Total Assets</span><span>{inr(bs.assets.total)}</span></div>
            </CardContent>
          </Card>
          <Card className="border-slate-200">
            <CardContent className="p-5">
              <div className="mb-3 text-sm font-semibold uppercase tracking-wide text-rose-600">Liabilities &amp; Equity</div>
              <div className="flex justify-between border-b border-slate-100 py-2.5 text-sm"><span className="text-slate-600">Payables (You'll Give)</span><b>{inr(bs.liabilities.payables)}</b></div>
              <div className="flex justify-between border-b border-slate-100 py-2.5 text-sm"><span className="text-slate-600">Retained Profit (P&amp;L)</span><b>{inr(bs.equity.retained_profit)}</b></div>
              <div className="flex justify-between border-b border-slate-100 py-2.5 text-sm"><span className="text-slate-600">Capital (balancing)</span><b>{inr(bs.equity.balancing)}</b></div>
              <div className="flex justify-between py-3 text-sm font-bold text-rose-600" data-testid="bs-liab-total"><span>Total Liabilities + Equity</span><span>{inr(bs.liabilities.total + bs.equity.balancing)}</span></div>
            </CardContent>
          </Card>
        </div>
      )}

      {key === "reports/profit" && (
        <div className="space-y-6" data-testid="profit-tab">
          <div className="flex justify-end"><ExportBtn kind="profit" testid="export-profit" /></div>
          {[
            { title: "Customer-wise Profit", rows: profit.customer_wise, cols: [{ label: "Customer" }, { label: "Invoices", key: "invoices" }, { label: "Sales", key: "sales" }, { label: "COGS", key: "cogs" }, { label: "Profit", key: "profit" }], key: "customer", testid: "profit-customer-table" },
            { title: "Item-wise Profit", rows: profit.item_wise, cols: [{ label: "Item" }, { label: "Qty Sold", key: "qty" }, { label: "Sales", key: "sales" }, { label: "COGS", key: "cogs" }, { label: "Profit", key: "profit" }], key: "item", testid: "profit-item-table" },
          ].map((t) => (
            <div key={t.key} className="rounded-lg border border-slate-200 bg-white">
              <div className="border-b border-slate-200 px-4 py-3 text-sm font-semibold text-slate-900">{t.title}</div>
              <Table>
                <TableHeader><TableRow>{t.cols.map((c, i) => c.key ? <SortTh key={c.label} t={`profit-${t.key}`} k={c.key} label={c.label} className="text-right" testid={`sort-profit-${t.key}-${c.key}`} /> : <TableHead key={c.label}>{c.label}</TableHead>)}</TableRow></TableHeader>
                <TableBody data-testid={t.testid}>
                  {applySortTo(`profit-${t.key}`, t.rows).map((r, i) => (
                    <TableRow key={i}>
                      <TableCell className="font-medium">{r[t.key]}</TableCell>
                      <TableCell className="text-right">{t.key === "customer" ? r.invoices : r.qty}</TableCell>
                      <TableCell className="text-right">{inr(r.sales)}</TableCell>
                      <TableCell className="text-right">{inr(r.cogs)}</TableCell>
                      <TableCell className="text-right font-semibold"><Money v={r.profit} /></TableCell>
                    </TableRow>
                  ))}
                  {t.rows.length === 0 && <TableRow><TableCell colSpan={5}><EmptyState compact title="No data in this period" /></TableCell></TableRow>}
                </TableBody>
              </Table>
            </div>
          ))}
        </div>
      )}

      {key === "reports/cash-flow" && cashFlow && (
        <div className="space-y-4" data-testid="cash-flow-page">
          <div className="grid grid-cols-2 gap-3 md:grid-cols-4" data-testid="cash-flow-kpis">
            <Card className="border-slate-200"><CardContent className="p-3"><div className="text-[11px] font-medium uppercase tracking-wide text-slate-500">Opening Balance</div><div className="mt-1 font-heading text-lg font-bold text-slate-900" data-testid="cf-opening">{inr(cashFlow.opening)}</div></CardContent></Card>
            <Card className="border-emerald-200 bg-emerald-50/40"><CardContent className="p-3"><div className="text-[11px] font-medium uppercase tracking-wide text-emerald-700">Money In</div><div className="mt-1 font-heading text-lg font-bold text-emerald-800" data-testid="cf-in">+{inr(cashFlow.total_in)}</div></CardContent></Card>
            <Card className="border-rose-200 bg-rose-50/40"><CardContent className="p-3"><div className="text-[11px] font-medium uppercase tracking-wide text-rose-700">Money Out</div><div className="mt-1 font-heading text-lg font-bold text-rose-700" data-testid="cf-out">-{inr(cashFlow.total_out)}</div></CardContent></Card>
            <Card className="border-slate-200"><CardContent className="p-3"><div className="text-[11px] font-medium uppercase tracking-wide text-slate-500">Closing Balance</div><div className="mt-1 font-heading text-lg font-bold text-slate-900" data-testid="cf-closing">{inr(cashFlow.closing)}</div></CardContent></Card>
          </div>
          <div className="rounded-lg border border-slate-200 bg-white">
            <Table>
              <TableHeader><TableRow><SortTh t="cash-flow" k="date" label="Date" testid="sort-cash-flow-date" /><SortTh t="cash-flow" k="opening" label="Opening" className="text-right" testid="sort-cash-flow-opening" /><SortTh t="cash-flow" k="inflow" label="Money In" className="text-right" testid="sort-cash-flow-in" /><SortTh t="cash-flow" k="outflow" label="Money Out" className="text-right" testid="sort-cash-flow-out" /><SortTh t="cash-flow" k="closing" label="Closing" className="text-right" testid="sort-cash-flow-closing" /></TableRow></TableHeader>
              <TableBody data-testid="cash-flow-table">
                {applySortTo("cash-flow", cashFlow.days).map((d) => (
                  <TableRow key={d.date} data-testid={`cf-row-${d.date}`}>
                    <TableCell>{d.date}</TableCell>
                    <TableCell className="text-right text-slate-500">{inr(d.opening)}</TableCell>
                    <TableCell className="text-right text-emerald-700">{d.inflow ? `+${inr(d.inflow)}` : "—"}</TableCell>
                    <TableCell className="text-right text-rose-600">{d.outflow ? `-${inr(d.outflow)}` : "—"}</TableCell>
                    <TableCell className="text-right font-medium">{inr(d.closing)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </div>
      )}

      {key === "reports/sale-report" && (
        <div className="space-y-4" data-testid="sale-report-page">
          <div className="flex justify-end"><ExportBtn kind="sales" testid="export-sale-report" /></div>
          <div className="rounded-lg border border-slate-200 bg-white">
            <Table>
              <TableHeader><TableRow><TableHead>Invoice No</TableHead><SortTh t="sale-report" k="date" label="Date" testid="sort-sale-report-date" /><TableHead>Customer</TableHead><SortTh t="sale-report" k="total" label="Total" className="text-right" testid="sort-sale-report-total" /><SortTh t="sale-report" k="paid" label="Paid" className="text-right" testid="sort-sale-report-paid" /><SortTh t="sale-report" k="due" label="Balance" className="text-right" testid="sort-sale-report-balance" /><TableHead>Status</TableHead></TableRow></TableHeader>
              <TableBody data-testid="sale-report-table">
                {applySortTo("sale-report", salesRows).map((r) => (
                  <TableRow key={r.id}>
                    <TableCell className="font-medium">{r.number}</TableCell>
                    <TableCell>{r.date}</TableCell>
                    <TableCell>{r.customer}</TableCell>
                    <TableCell className="text-right">{inr(r.total)}</TableCell>
                    <TableCell className="text-right text-emerald-700">{inr(r.paid)}</TableCell>
                    <TableCell className={`text-right font-medium ${r.due > 0.009 ? "text-rose-600" : "text-slate-400"}`}>{inr(r.due)}</TableCell>
                    <TableCell><Badge className={`capitalize ${r.status === "paid" ? "bg-emerald-100 text-emerald-700 hover:bg-emerald-100" : r.status === "partial" ? "bg-amber-100 text-amber-700 hover:bg-amber-100" : "bg-rose-100 text-rose-700 hover:bg-rose-100"}`}>{r.status}</Badge></TableCell>
                  </TableRow>
                ))}
                {salesRows.length === 0 && <TableRow><TableCell colSpan={7}><EmptyState compact title="No sales in this period" /></TableCell></TableRow>}
              </TableBody>
            </Table>
          </div>
        </div>
      )}

      {key === "reports/all-transactions" && (
        <div className="space-y-4" data-testid="all-transactions-page">
          <div className="rounded-lg border border-slate-200 bg-white">
            <Table>
              <TableHeader><TableRow><SortTh t="all-transactions" k="date" label="Date" testid="sort-all-transactions-date" /><TableHead>Type</TableHead><TableHead>Ref No.</TableHead><TableHead>Party</TableHead><SortTh t="all-transactions" k="amount" label="Amount" className="text-right" testid="sort-all-transactions-amount" /></TableRow></TableHeader>
              <TableBody data-testid="all-transactions-table">
                {applySortTo("all-transactions", allTxns).map((t, i) => (
                  <TableRow key={i}>
                    <TableCell>{t.date}</TableCell>
                    <TableCell><Badge variant="outline">{t.kind}</Badge></TableCell>
                    <TableCell className="font-medium">{t.ref || "-"}</TableCell>
                    <TableCell>{t.party || "-"}</TableCell>
                    <TableCell className={`text-right font-medium ${t.amount >= 0 ? "text-emerald-700" : "text-rose-600"}`}>{t.amount >= 0 ? "+" : ""}{inr(t.amount)}</TableCell>
                  </TableRow>
                ))}
                {allTxns.length === 0 && <TableRow><TableCell colSpan={5}><EmptyState compact title="No transactions in this period" /></TableCell></TableRow>}
              </TableBody>
            </Table>
          </div>
        </div>
      )}

      {key === "reports/daybook" && (
        <div className="space-y-4" data-testid="daybook-page">
          <div className="flex flex-wrap items-center gap-3">
            <Input type="date" value={daybookDate} onChange={async (e) => { setDaybookDate(e.target.value); try { setDaybook((await API.get("/accounting/reports/daybook", { params: { date: e.target.value } })).data); } catch {} }} className="w-44" data-testid="daybook-date-input" />
            {daybook && (
              <Badge className={`px-3 py-1 text-sm ${daybook.net >= 0 ? "bg-emerald-100 text-emerald-700 hover:bg-emerald-100" : "bg-rose-100 text-rose-600 hover:bg-rose-100"}`} data-testid="daybook-net">Net: {daybook.net >= 0 ? "+" : ""}{inr(daybook.net)}</Badge>
            )}
          </div>
          {daybook && Object.keys(daybook.totals).length > 0 && (
            <div className="flex flex-wrap gap-2" data-testid="daybook-totals">
              {Object.entries(daybook.totals).map(([k, v]) => (
                <span key={k} className="rounded-md border border-slate-200 bg-white px-3 py-1.5 text-xs" data-testid={`daybook-total-${k.replace(/\W+/g, "-")}`}><b>{k}</b>: <span className={v >= 0 ? "text-emerald-700" : "text-rose-600"}>{inr(v)}</span></span>
              ))}
            </div>
          )}
          <div className="rounded-lg border border-slate-200 bg-white">
            <Table>
              <TableHeader><TableRow><TableHead>Type</TableHead><TableHead>Ref No.</TableHead><TableHead>Party</TableHead><SortTh t="daybook" k="amount" label="Amount" className="text-right" testid="sort-daybook-amount" /></TableRow></TableHeader>
              <TableBody data-testid="daybook-table">
                {applySortTo("daybook", daybook?.transactions || []).map((t, i) => (
                  <TableRow key={i}>
                    <TableCell><Badge variant="outline">{t.kind}</Badge></TableCell>
                    <TableCell className="font-medium">{t.ref || "-"}</TableCell>
                    <TableCell>{t.party || "-"}</TableCell>
                    <TableCell className={`text-right font-medium ${t.amount >= 0 ? "text-emerald-700" : "text-rose-600"}`}>{t.amount >= 0 ? "+" : ""}{inr(t.amount)}</TableCell>
                  </TableRow>
                ))}
                {(daybook?.transactions || []).length === 0 && <TableRow><TableCell colSpan={4}><EmptyState compact title="No transactions on this date" /></TableCell></TableRow>}
              </TableBody>
            </Table>
          </div>
        </div>
      )}

      {key === "reports/party-statement" && (
        <div className="space-y-4" data-testid="party-statement-page">
          <div className="flex flex-wrap items-center gap-2">
            <Select value={stmtParty} onValueChange={(v) => { setStmtParty(v); loadLedger(v); }}>
              <SelectTrigger className="w-72" data-testid="stmt-party-select"><SelectValue placeholder="Select party" /></SelectTrigger>
              <SelectContent>{parties.map((p) => <SelectItem key={p.name} value={p.name}>{p.name}</SelectItem>)}</SelectContent>
            </Select>
            {stmtParty && (
              <>
                <Button variant="outline" size="sm" onClick={() => downloadExcel(`/accounting/parties/ledger/export?name=${encodeURIComponent(stmtParty)}`, `statement-${stmtParty}.xlsx`)} data-testid="stmt-excel"><FileSpreadsheet className="mr-1 h-3.5 w-3.5" /> Excel</Button>
                <Button variant="outline" size="sm" onClick={() => window.print()} data-testid="stmt-print"><Printer className="mr-1 h-3.5 w-3.5" /> Print</Button>
              </>
            )}
          </div>
          {stmtParty && (
            <div className="rounded-lg border border-slate-200 bg-white" data-testid="stmt-table-wrap">
              <Table>
                <TableHeader><TableRow><SortTh t="party-statement" k="date" label="Date" testid="sort-party-statement-date" /><TableHead>Type</TableHead><TableHead>Number</TableHead><SortTh t="party-statement" k="total" label="Total" className="text-right" testid="sort-party-statement-total" /><SortTh t="party-statement" k="paid" label="Paid" className="text-right" testid="sort-party-statement-paid" /><SortTh t="party-statement" k="balance" label="Balance" className="text-right" testid="sort-party-statement-balance" /></TableRow></TableHeader>
                <TableBody data-testid="stmt-table">
                  {applySortTo("party-statement", ledger).map((t, i) => (
                    <TableRow key={i}>
                      <TableCell>{t.date}</TableCell>
                      <TableCell><Badge variant="outline">{t.kind}</Badge></TableCell>
                      <TableCell className="font-medium">{t.ref}</TableCell>
                      <TableCell className="text-right"><Money v={t.total} /></TableCell>
                      <TableCell className="text-right text-slate-500">{t.paid ? inr(t.paid) : "-"}</TableCell>
                      <TableCell className={`text-right font-medium ${t.balance > 0 ? "text-rose-600" : "text-slate-400"}`}>{t.balance ? inr(t.balance) : "—"}</TableCell>
                    </TableRow>
                  ))}
                  {ledger.length === 0 && <TableRow><TableCell colSpan={6}><EmptyState compact title="No transactions with this party" /></TableCell></TableRow>}
                </TableBody>
              </Table>
            </div>
          )}
        </div>
      )}

      {key === "sales/performa" && (
        <div data-testid="performa-tab">
          <Documents fixedType="quotation" />
        </div>
      )}

      {key === "sales/invoices" && (
        <div data-testid="sales-tab">
          <Documents fixedType="invoice" />
        </div>
      )}

      {(key === "sales/payment-in" || key === "stock/payment-out") && (
        <div className="space-y-3" data-testid="payments-tab">
          <div className="flex justify-end gap-2">
            <ExportBtn kind="payments" testid="export-payments" />
            <Button size="sm" className="bg-indigo-600 hover:bg-indigo-700" onClick={() => { setEditPayId(null); setPayForm({ ...emptyPayment, type: key === "sales/payment-in" ? "receipt" : "payment", account_id: accounts[0]?.id || "" }); setDlg("payment"); }} data-testid="add-payment-button">
              <Plus className="mr-1 h-3.5 w-3.5" /> {key === "sales/payment-in" ? "Add Payment-In" : "Add Payment-Out"}
            </Button>
          </div>
          <div className="rounded-lg border border-slate-200 bg-white">
            <Table>
              <TableHeader><TableRow><SortTh t="payments" k="date" label="Date" testid="sort-payments-date" /><TableHead>Party</TableHead><TableHead>Account</TableHead><SortTh t="payments" k="amount" label="Amount" className="text-right" testid="sort-payments-amount" /><TableHead>Ref / Note</TableHead><TableHead></TableHead></TableRow></TableHeader>
              <TableBody data-testid="payments-table">
                {applySortTo("payments", paymentRows).map((p) => (
                  <TableRow key={p.id}>
                    <TableCell>{p.date}</TableCell>
                    <TableCell className="font-medium">{p.party}</TableCell>
                    <TableCell className="text-slate-500">{p.account_name || "-"}</TableCell>
                    <TableCell className={`text-right font-medium ${p.type === "receipt" ? "text-emerald-700" : "text-rose-600"}`}>{p.type === "receipt" ? "+" : "-"}{inr(p.amount)}</TableCell>
                    <TableCell className="max-w-40 truncate text-slate-500">{[p.ref, p.note].filter(Boolean).join(" · ") || "-"}</TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-1">
                        <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => openEditPayment(p)} data-testid={`payment-edit-${p.id}`} aria-label="Edit payment"><Pencil className="h-3.5 w-3.5 text-slate-500" /></Button>
                        <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => remove("payments", p.id)} data-testid={`payment-delete-${p.id}`} aria-label="Delete"><Trash2 className="h-3.5 w-3.5 text-rose-500" /></Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
                {paymentRows.length === 0 && <TableRow><TableCell colSpan={6}><EmptyState compact title="No entries yet" hint="Record a payment above to see it here." /></TableCell></TableRow>}
              </TableBody>
            </Table>
          </div>
        </div>
      )}

      {key === "sales/sale-return" && (
        <div className="space-y-3" data-testid="sale-return-tab">
          <div className="flex justify-end gap-2">
            <ExportBtn kind="sale-returns" testid="export-sale-returns" />
            <Button size="sm" className="bg-indigo-600 hover:bg-indigo-700" onClick={() => setDlg("sale-return")} data-testid="add-sale-return-button"><Plus className="mr-1 h-3.5 w-3.5" /> New Sale Return</Button>
          </div>
          <div className="rounded-lg border border-slate-200 bg-white">
            <Table>
              <TableHeader><TableRow><TableHead>Return No</TableHead><SortTh t="sale-returns" k="created_at" label="Date" testid="sort-sale-returns-date" /><TableHead>Invoice</TableHead><TableHead>Customer</TableHead><SortTh t="sale-returns" k="items_count" label="Items" className="text-right" testid="sort-sale-returns-items" /><SortTh t="sale-returns" k="total" label="Amount" className="text-right" testid="sort-sale-returns-amount" /><TableHead>Reason</TableHead></TableRow></TableHeader>
              <TableBody data-testid="sale-returns-table">
                {applySortTo("sale-returns", saleReturns.map((r) => ({ ...r, items_count: r.items?.length || 0 }))).map((r) => (
                  <TableRow key={r.id}>
                    <TableCell className="font-medium">{r.number}</TableCell>
                    <TableCell>{String(r.created_at).slice(0, 10)}</TableCell>
                    <TableCell>{r.invoice_number}</TableCell>
                    <TableCell>{r.customer}</TableCell>
                    <TableCell className="text-right">{r.items?.length}</TableCell>
                    <TableCell className="text-right font-medium text-rose-600">-{inr(r.total)}</TableCell>
                    <TableCell className="max-w-40 truncate text-slate-500">{r.reason || "-"}</TableCell>
                  </TableRow>
                ))}
                {saleReturns.length === 0 && <TableRow><TableCell colSpan={7}><EmptyState compact title="No sale returns yet" /></TableCell></TableRow>}
              </TableBody>
            </Table>
          </div>
        </div>
      )}

      {key === "stock/purchases" && (
        <div className="space-y-6" data-testid="purchases-tab">
          <div className="grid grid-cols-[1fr_auto_1fr_auto_1fr] items-stretch gap-2" data-testid="purchase-kpis">
            <Card className="border-emerald-200 bg-emerald-50/40"><CardContent className="p-3"><div className="text-[11px] font-medium uppercase tracking-wide text-emerald-700">Paid</div><div className="mt-1 font-heading text-lg font-bold text-emerald-800" data-testid="purchase-kpi-paid">{inr(purchases.reduce((a, p) => a + (p.paid ?? 0), 0))}</div></CardContent></Card>
            <div className="self-center font-heading text-lg font-bold text-slate-400">+</div>
            <Card className="border-rose-200 bg-rose-50/40"><CardContent className="p-3"><div className="text-[11px] font-medium uppercase tracking-wide text-rose-700">Unpaid</div><div className="mt-1 font-heading text-lg font-bold text-rose-700" data-testid="purchase-kpi-unpaid">{inr(purchases.reduce((a, p) => a + (p.due ?? p.total ?? 0), 0))}</div></CardContent></Card>
            <div className="self-center font-heading text-lg font-bold text-slate-400">=</div>
            <Card className="border-slate-200"><CardContent className="p-3"><div className="text-[11px] font-medium uppercase tracking-wide text-slate-500">Total</div><div className="mt-1 font-heading text-lg font-bold text-slate-900" data-testid="purchase-kpi-total">{inr(purchases.reduce((a, p) => a + (p.total ?? 0), 0))}</div></CardContent></Card>
          </div>
          <div className="flex items-center justify-between">
            <p className="text-xs text-slate-400">Challan bills are auto-created on goods receipt · manual bills for direct vendors</p>
            <div className="flex gap-2">
              <Select value={purStatusFilter} onValueChange={setPurStatusFilter}>
                <SelectTrigger className="h-8 w-32" data-testid="purchase-status-filter"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {["all", "unpaid", "partial", "paid", "cancelled"].map((s) => <SelectItem key={s} value={s} className="capitalize">{s === "all" ? "All Status" : s}</SelectItem>)}
                </SelectContent>
              </Select>
              <ExportBtn kind="purchases" testid="export-purchases" />
              <Button size="sm" className="bg-indigo-600 hover:bg-indigo-700" onClick={() => { setEditPurId(null); setPurForm(emptyPurchase); setDlg("purchase"); }} data-testid="add-purchase-button"><Plus className="mr-1 h-3.5 w-3.5" /> New Purchase (Direct Vendor)</Button>
            </div>
          </div>
          <div className="rounded-lg border border-slate-200 bg-white">
            <Table>
              <TableHeader>
                <TableRow><TableHead>Bill No</TableHead><SortTh t="purchases" k="sort_date" label="Date" testid="sort-purchases-date" /><TableHead>Vendor</TableHead><TableHead>Source</TableHead><SortTh t="purchases" k="items_count" label="Items" className="text-right" testid="sort-purchases-items" /><SortTh t="purchases" k="total" label="Total" className="text-right" testid="sort-purchases-total" /><SortTh t="purchases" k="paid" label="Paid" className="text-right" testid="sort-purchases-paid" /><SortTh t="purchases" k="due" label="Balance" className="text-right" testid="sort-purchases-balance" /><TableHead>Status</TableHead><TableHead className="text-right">Actions</TableHead></TableRow>
              </TableHeader>
              <TableBody data-testid="purchases-table">
                {applySortTo("purchases", purchases.map((p) => ({ ...p, sort_date: (p.date || String(p.created_at)).slice(0, 10), items_count: p.items?.length || 0 })).filter((p) => purStatusFilter === "all" || (p.pay_status || "unpaid") === purStatusFilter)).map((p) => (
                  <TableRow key={p.id}>
                    <TableCell className="font-medium">{p.number}</TableCell>
                    <TableCell>{(p.date || String(p.created_at)).slice(0, 10)}</TableCell>
                    <TableCell>{p.vendor}</TableCell>
                    <TableCell>{p.challan_number ? <Badge variant="outline">Challan {p.challan_number}</Badge> : <Badge className="bg-slate-100 text-slate-600 hover:bg-slate-100">Manual</Badge>}</TableCell>
                    <TableCell className="text-right">{p.items?.length}</TableCell>
                    <TableCell className="text-right font-medium">{inr(p.total)}</TableCell>
                    <TableCell className="text-right text-emerald-700">{inr(p.paid ?? 0)}</TableCell>
                    <TableCell className={`text-right font-medium ${(p.due ?? 0) > 0.009 ? "text-rose-600" : "text-slate-400"}`} data-testid={`purchase-due-${p.id}`}>{inr(p.due ?? p.total)}</TableCell>
                    <TableCell><Badge className={`capitalize ${(p.pay_status || "unpaid") === "paid" ? "bg-emerald-100 text-emerald-700 hover:bg-emerald-100" : (p.pay_status === "partial" ? "bg-amber-100 text-amber-700 hover:bg-amber-100" : "bg-rose-100 text-rose-700 hover:bg-rose-100")}`} data-testid={`purchase-status-${p.id}`}>{p.pay_status || "unpaid"}</Badge></TableCell>
                    <TableCell>
                      <div className="flex items-center justify-end gap-1">
                        <Button size="sm" variant="outline" className="h-7 border-rose-300 text-rose-600 hover:bg-rose-50" onClick={() => { setPrForm({ purchase: p, qtys: {}, reason: "" }); setDlg("purchase-return"); }} data-testid={`purchase-return-${p.id}`}>
                          <RotateCcw className="mr-1 h-3.5 w-3.5" /> Return
                        </Button>
                        {!p.challan_id && (
                          <>
                            <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => openEditPurchase(p)} data-testid={`purchase-edit-${p.id}`} aria-label="Edit"><Pencil className="h-3.5 w-3.5 text-slate-500" /></Button>
                            <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => remove("purchases", p.id)} data-testid={`purchase-delete-${p.id}`} aria-label="Delete"><Trash2 className="h-3.5 w-3.5 text-rose-500" /></Button>
                          </>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
                {purchases.length === 0 && <TableRow><TableCell colSpan={10}><EmptyState compact title="No purchase bills yet" /></TableCell></TableRow>}
              </TableBody>
            </Table>
          </div>
          <div className="rounded-lg border border-slate-200 bg-white">
            <div className="border-b border-slate-200 px-4 py-3 text-sm font-semibold text-slate-900">Purchase Returns</div>
            <Table>
              <TableHeader><TableRow><TableHead>Return No</TableHead><SortTh t="purchase-returns" k="created_at" label="Date" testid="sort-purchase-returns-date" /><TableHead>Bill</TableHead><TableHead>Vendor</TableHead><SortTh t="purchase-returns" k="total" label="Amount" className="text-right" testid="sort-purchase-returns-amount" /><TableHead>Reason</TableHead></TableRow></TableHeader>
              <TableBody data-testid="purchase-returns-table">
                {applySortTo("purchase-returns", purchaseReturns).map((r) => (
                  <TableRow key={r.id}>
                    <TableCell className="font-medium">{r.number}</TableCell>
                    <TableCell>{String(r.created_at).slice(0, 10)}</TableCell>
                    <TableCell>{r.purchase_number}</TableCell>
                    <TableCell>{r.vendor}</TableCell>
                    <TableCell className="text-right font-medium text-rose-600">-{inr(r.total)}</TableCell>
                    <TableCell className="max-w-40 truncate text-slate-500">{r.reason || "-"}</TableCell>
                  </TableRow>
                ))}
                {purchaseReturns.length === 0 && <TableRow><TableCell colSpan={6}><EmptyState compact title="No purchase returns" /></TableCell></TableRow>}
              </TableBody>
            </Table>
          </div>
        </div>
      )}

      {key === "stock/stock" && (
        <div className="space-y-3" data-testid="b2b-stock-tab">
          <div className="flex items-center justify-between">
            <p className="text-xs text-slate-400">Valued at weighted-average challan purchase rate</p>
            <ExportBtn kind="stock" testid="export-stock" />
          </div>
          <div className="rounded-lg border border-slate-200 bg-white">
            <Table>
              <TableHeader><TableRow><TableHead>Product</TableHead><TableHead>SKU</TableHead><SortTh t="b2b-stock" k="qty" label="Stock Qty" className="text-right" testid="sort-b2b-stock-qty" /><SortTh t="b2b-stock" k="reserved" label="Reserved" className="text-right" testid="sort-b2b-stock-reserved" /><SortTh t="b2b-stock" k="available" label="Available" className="text-right" testid="sort-b2b-stock-available" /><SortTh t="b2b-stock" k="avg_rate" label="Avg Rate" className="text-right" testid="sort-b2b-stock-rate" /><SortTh t="b2b-stock" k="value" label="Value" className="text-right" testid="sort-b2b-stock-value" /></TableRow></TableHeader>
              <TableBody data-testid="b2b-stock-table">
                {applySortTo("b2b-stock", stock).map((r) => (
                  <TableRow key={r.product_id} data-testid={`b2b-stock-row-${r.product_id}`}>
                    <TableCell className="font-medium">{r.name}</TableCell>
                    <TableCell className="text-slate-500">{r.sku || "-"}</TableCell>
                    <TableCell className={`text-right ${r.qty < 0 ? "font-semibold text-rose-600" : ""}`} data-testid={`b2b-stock-qty-${r.product_id}`}>{r.qty} {r.unit}</TableCell>
                    <TableCell className="text-right text-slate-500">{r.reserved ?? 0}</TableCell>
                    <TableCell className={`text-right ${(r.available ?? 0) < 0 ? "font-semibold text-amber-600" : ""}`} data-testid={`b2b-stock-available-${r.product_id}`}>{r.available ?? r.qty}</TableCell>
                    <TableCell className="text-right">{inr(r.avg_rate)}</TableCell>
                    <TableCell className="text-right font-medium">{inr(r.value)}</TableCell>
                  </TableRow>
                ))}
                {stock.length === 0 && <TableRow><TableCell colSpan={7}><EmptyState compact title="No stock in the B2B warehouse yet" /></TableCell></TableRow>}
              </TableBody>
            </Table>
          </div>
        </div>
      )}

      {key === "accounts/expenses" && (
        <div className="space-y-4" data-testid="expenses-tab">
          <div className="flex justify-end gap-2">
            <ExportBtn kind="expenses" testid="export-expenses" />
            <Button size="sm" className="bg-indigo-600 hover:bg-indigo-700" onClick={() => openExpenseDialog()} data-testid="add-expense-button"><Plus className="mr-1 h-3.5 w-3.5" /> Add Expense</Button>
          </div>
          {catSummary.length > 0 && (
            <div className="grid grid-cols-2 gap-3 md:grid-cols-4 xl:grid-cols-6" data-testid="expense-categories">
              {catSummary.map(([cat, amt]) => (
                <Card key={cat} className="border-slate-200"><CardContent className="p-3.5">
                  <div className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">{cat}</div>
                  <div className="mt-1 font-heading text-base font-bold text-slate-900">{inr(amt)}</div>
                </CardContent></Card>
              ))}
            </div>
          )}
          <div className="flex flex-wrap items-center gap-2" data-testid="expense-view-tabs">
            {[["items", "Items"], ["category", "Category"]].map(([k, label]) => (
              <button key={k} onClick={() => setExpView(k)} className={`rounded-full px-4 py-1.5 text-sm font-medium transition-colors ${expView === k ? "bg-indigo-600 text-white" : "border border-slate-200 bg-white text-slate-600 hover:bg-slate-50"}`} data-testid={`expense-view-${k}`}>{label}</button>
            ))}
          </div>

          {expView === "category" && (
            <div className="space-y-4" data-testid="expense-category-panel">
              <div className="flex flex-wrap items-end gap-2 rounded-lg border border-slate-200 bg-white p-3">
                <div><Label className="text-xs">New category</Label><Input value={newCatName} onChange={(e) => setNewCatName(e.target.value)} placeholder="e.g. Transport" className="mt-1 h-9 w-48" data-testid="new-expense-category-input" /></div>
                <div>
                  <Label className="text-xs">Type (P&L placement)</Label>
                  <Select value={newCatKind} onValueChange={setNewCatKind}>
                    <SelectTrigger className="mt-1 h-9 w-36" data-testid="new-expense-category-kind"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="direct">Direct</SelectItem>
                      <SelectItem value="indirect">Indirect</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <Button size="sm" className="bg-indigo-600 hover:bg-indigo-700" data-testid="add-expense-category-button"
                  onClick={async () => {
                    if (!newCatName.trim()) return toast.error("Category name required");
                    try {
                      await API.post("/accounting/expense-categories", { name: newCatName.trim(), kind: newCatKind });
                      toast.success("Category saved");
                      setNewCatName("");
                      load();
                    } catch (e) { toast.error(e.response?.data?.detail || "Could not save category"); }
                  }}>Add Category</Button>
              </div>
              <div className="grid grid-cols-1 gap-3 md:grid-cols-3" data-testid="expense-category-cards">
                {expCatSummary.map((c) => (
                  <Card key={c.name} className="border-slate-200" data-testid={`expense-category-${c.name.replace(/\W+/g, "-")}`}>
                    <CardContent className="p-4">
                      <div className="flex items-center justify-between">
                        <span className="text-sm font-semibold text-slate-900">{c.name}</span>
                        <Badge className={c.kind === "direct" ? "bg-indigo-100 text-indigo-700 hover:bg-indigo-100" : "bg-slate-100 text-slate-600 hover:bg-slate-100"} data-testid={`expense-category-kind-${c.name.replace(/\W+/g, "-")}`}>{c.kind}</Badge>
                      </div>
                      <div className="mt-2 flex items-center justify-between text-sm">
                        <span className="text-slate-500">{c.count} {c.count === 1 ? "entry" : "entries"}</span>
                        <span className="font-heading font-bold text-slate-900">Total {inr(c.total)}</span>
                      </div>
                      <div className="mt-1 text-xs text-slate-400">Balance {inr(c.total)}</div>
                    </CardContent>
                  </Card>
                ))}
                {expCatSummary.length === 0 && <EmptyState compact title="No expense categories yet" />}
              </div>
            </div>
          )}

          {expView === "items" && (
          <div className="rounded-lg border border-slate-200 bg-white">
            <Table>
              <TableHeader><TableRow><TableHead>Exp No.</TableHead><SortTh t="expenses" k="date" label="Date" testid="sort-expenses-date" /><TableHead>Category</TableHead><SortTh t="expenses" k="amount" label="Amount" className="text-right" testid="sort-expenses-amount" /><TableHead>Note</TableHead><TableHead></TableHead></TableRow></TableHeader>
              <TableBody data-testid="expenses-table">
                {applySortTo("expenses", expenses).map((e) => (
                  <TableRow key={e.id}>
                    <TableCell className="font-medium text-slate-500">{e.exp_no || "-"}</TableCell>
                    <TableCell>{e.date}</TableCell>
                    <TableCell className="font-medium">{e.category}</TableCell>
                    <TableCell className="text-right">{inr(e.amount)}</TableCell>
                    <TableCell className="max-w-48 truncate text-slate-500">{e.note || "-"}</TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-1">
                        <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => openExpenseDialog(e)} data-testid={`expense-edit-${e.id}`} aria-label="Edit expense"><Pencil className="h-3.5 w-3.5 text-slate-500" /></Button>
                        <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => remove("expenses", e.id)} data-testid={`expense-delete-${e.id}`} aria-label="Delete expense"><Trash2 className="h-3.5 w-3.5 text-rose-500" /></Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
                {expenses.length === 0 && <TableRow><TableCell colSpan={6}><EmptyState compact title="No expenses yet" hint="Add your first expense above." /></TableCell></TableRow>}
              </TableBody>
            </Table>
          </div>
          )}
        </div>
      )}

      {key === "accounts/parties" && (
        <div className="space-y-4" data-testid="parties-tab">
          <div className="flex justify-end gap-2">
            <ExportBtn kind="parties" testid="export-parties" />
            <Button size="sm" variant="outline" onClick={() => setDlg("import-parties")} data-testid="import-parties-button"><Users2 className="mr-1 h-3.5 w-3.5" /> Import Contacts</Button>
            <Button size="sm" className="bg-indigo-600 hover:bg-indigo-700" onClick={() => { setEditPartyId(null); setPartyForm(emptyParty); setDlg("party"); }} data-testid="add-party-button"><Plus className="mr-1 h-3.5 w-3.5" /> Add New Party</Button>
          </div>
          <div className="rounded-lg border border-slate-200 bg-white">
            <Table>
              <TableHeader><TableRow><TableHead>Party</TableHead><TableHead>Type</TableHead><SortTh t="parties" k="billed" label="Billed / Purchased" className="text-right" testid="sort-parties-billed" /><SortTh t="parties" k="received" label="Received / Paid" className="text-right" testid="sort-parties-received" /><SortTh t="parties" k="advance" label="Advance (Unused)" className="text-right" testid="sort-parties-advance" /><SortTh t="parties" k="balance" label="Balance" className="text-right" testid="sort-parties-balance" /><TableHead className="text-right">Actions</TableHead></TableRow></TableHeader>
              <TableBody data-testid="parties-table">
                {applySortTo("parties", parties.map((p) => ({ ...p, advance: Math.max(p.advance_in || 0, p.advance_out || 0) }))).map((p) => (
                  <TableRow key={p.name} className="cursor-pointer hover:bg-slate-50" onClick={() => loadLedger(p.name)} data-testid={`party-row-${p.name.replace(/\W+/g, "-")}`}>
                    <TableCell>
                      <div className="font-medium text-slate-900">{p.name}</div>
                      <div className="text-xs text-slate-400">{p.phone || ""}</div>
                    </TableCell>
                    <TableCell><Badge variant="outline" className="capitalize">{p.type}</Badge></TableCell>
                    <TableCell className="text-right text-sm">{inr(p.billed)} / {inr(p.purchased)}</TableCell>
                    <TableCell className="text-right text-sm">{inr(p.received)} / {inr(p.paid)}</TableCell>
                    <TableCell className="text-right text-sm" data-testid={`party-advance-${p.name.replace(/\W+/g, "-")}`}>
                      {p.advance_in > 0 && <div className="text-emerald-700">+{inr(p.advance_in)} <span className="text-[10px] text-slate-400">from customer</span></div>}
                      {p.advance_out > 0 && <div className="text-rose-600">-{inr(p.advance_out)} <span className="text-[10px] text-slate-400">paid to vendor</span></div>}
                      {!p.advance_in && !p.advance_out && <span className="text-slate-300">—</span>}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className={`font-semibold ${p.direction === "get" ? "text-emerald-700" : p.direction === "give" ? "text-rose-600" : "text-slate-400"}`}>{inr(Math.abs(p.balance))}</div>
                      <div className="text-[11px] text-slate-400">{p.direction === "get" ? "You'll Get" : p.direction === "give" ? "You'll Give" : "Settled"}</div>
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-1">
                        {p.phone && (
                          <Button variant="ghost" size="icon" className="h-7 w-7" onClick={(e) => { e.stopPropagation(); window.open(`https://wa.me/${String(p.phone).replace(/\D/g, "")}?text=${encodeURIComponent(`Hello ${p.name}, this is regarding your account with us.`)}`, "_blank"); }} data-testid={`party-whatsapp-${p.name.replace(/\W+/g, "-")}`} aria-label="WhatsApp">
                            <MessageCircle className="h-3.5 w-3.5 text-[#25D366]" />
                          </Button>
                        )}
                        <Button variant="ghost" size="icon" className="h-7 w-7" onClick={(e) => { e.stopPropagation(); loadLedger(p.name); }} data-testid={`party-history-${p.name.replace(/\W+/g, "-")}`} aria-label="History">
                          <RotateCcw className="h-3.5 w-3.5 text-indigo-500" />
                        </Button>
                        {p.party_id && (
                          <Button variant="ghost" size="icon" className="h-7 w-7" onClick={(e) => { e.stopPropagation(); if (window.confirm(`Delete party ${p.name}? Only parties with no transaction history should be deleted.`)) remove("parties", p.party_id); }} data-testid={`party-delete-${p.name.replace(/\W+/g, "-")}`} aria-label="Delete party">
                            <Trash2 className="h-3.5 w-3.5 text-rose-500" />
                          </Button>
                        )}
                        {p.balance !== 0 && (
                          <Button size="sm" variant="outline" className="h-7" onClick={(e) => { e.stopPropagation(); partyPayment(p); }} data-testid={`party-payment-${p.name.replace(/\W+/g, "-")}`}>
                            {p.balance > 0 ? "Take Payment" : "Make Payment"}
                          </Button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
                {parties.length === 0 && <TableRow><TableCell colSpan={7}><EmptyState compact title="No parties yet" hint="Customers appear automatically; add vendors manually." /></TableCell></TableRow>}
              </TableBody>
            </Table>
          </div>
          {selParty && (
            <div className="rounded-lg border border-slate-200 bg-white" data-testid="party-ledger">
              <div className="flex flex-wrap items-center justify-between gap-2 border-b border-slate-200 px-4 py-3">
                <div className="flex items-center gap-2 text-sm font-semibold text-slate-900"><Users2 className="h-4 w-4 text-indigo-500" /> Transactions — {selParty}
                  {partySummary?.advance_in > 0 && <Badge className="bg-emerald-100 text-emerald-700 hover:bg-emerald-100" data-testid="party-ledger-advance-in">Advance in {inr(partySummary.advance_in)}</Badge>}
                  {partySummary?.advance_out > 0 && <Badge className="bg-rose-100 text-rose-700 hover:bg-rose-100" data-testid="party-ledger-advance-out">Advance paid {inr(partySummary.advance_out)}</Badge>}
                </div>
                <div className="flex items-center gap-2">
                  <Input value={ledgerQuery} onChange={(e) => setLedgerQuery(e.target.value)} placeholder="Search type / ref..." className="h-8 w-44" data-testid="party-ledger-search" />
                  <Button variant="outline" size="sm" className="h-8" onClick={() => downloadExcel(`/accounting/parties/ledger/export?name=${encodeURIComponent(selParty)}`, `ledger-${selParty}.xlsx`)} data-testid="party-ledger-excel"><FileSpreadsheet className="mr-1 h-3.5 w-3.5" /> Excel</Button>
                  <Button variant="outline" size="sm" className="h-8" onClick={() => window.print()} data-testid="party-ledger-print"><Printer className="mr-1 h-3.5 w-3.5" /> Print</Button>
                  <Button variant="ghost" size="sm" onClick={() => setSelParty(null)} data-testid="party-ledger-close">Close</Button>
                </div>
              </div>
              <Table>
                <TableHeader><TableRow><SortTh t="party-ledger" k="date" label="Date" testid="sort-party-ledger-date" /><TableHead>Type</TableHead><TableHead>Number</TableHead><SortTh t="party-ledger" k="total" label="Total" className="text-right" testid="sort-party-ledger-total" /><SortTh t="party-ledger" k="paid" label="Paid" className="text-right" testid="sort-party-ledger-paid" /><SortTh t="party-ledger" k="balance" label="Balance" className="text-right" testid="sort-party-ledger-balance" /><TableHead>Status</TableHead></TableRow></TableHeader>
                <TableBody data-testid="party-ledger-table">
                  {applySortTo("party-ledger", ledger.filter((t) => { const q = ledgerQuery.trim().toLowerCase(); return !q || (t.kind || "").toLowerCase().includes(q) || (t.ref || "").toLowerCase().includes(q); })).map((t, i) => (
                    <TableRow key={i}>
                      <TableCell>{t.date}</TableCell>
                      <TableCell><Badge variant="outline">{t.kind}</Badge></TableCell>
                      <TableCell className="font-medium">{t.ref}</TableCell>
                      <TableCell className="text-right"><Money v={t.total} /></TableCell>
                      <TableCell className="text-right text-slate-500">{t.paid ? inr(t.paid) : "-"}</TableCell>
                      <TableCell className={`text-right font-medium ${t.balance > 0 ? "text-rose-600" : "text-slate-400"}`}>{t.balance ? inr(t.balance) : "—"}</TableCell>
                      <TableCell data-testid={`ledger-status-${i}`}>{["Sale Invoice", "Purchase"].includes(t.kind) ? <Badge className={t.balance > 0.009 ? (t.paid > 0 ? "bg-amber-100 text-amber-700 hover:bg-amber-100" : "bg-rose-100 text-rose-700 hover:bg-rose-100") : "bg-emerald-100 text-emerald-700 hover:bg-emerald-100"}>{t.balance > 0.009 ? (t.paid > 0 ? "Partial" : "Unpaid") : "Paid"}</Badge> : <span className="text-slate-400">—</span>}</TableCell>
                    </TableRow>
                  ))}
                  {ledger.length === 0 && <TableRow><TableCell colSpan={7}><EmptyState compact title="No transactions with this party yet" /></TableCell></TableRow>}
                </TableBody>
              </Table>
            </div>
          )}
        </div>
      )}

      {key === "accounts/cash" && (
        <div className="space-y-4" data-testid="accounts-tab">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div className="flex gap-2" data-testid="cash-kind-tabs">
              {[["bank", "Bank Accounts"], ["cash", "Cash In Hand"]].map(([k, label]) => (
                <button key={k} onClick={() => setCashTab(k)} className={`rounded-full px-4 py-1.5 text-sm font-medium transition-colors ${cashTab === k ? "bg-indigo-600 text-white" : "border border-slate-200 bg-white text-slate-600 hover:bg-slate-50"}`} data-testid={`cash-tab-${k}`}>{label}</button>
              ))}
            </div>
            <div className="flex gap-2">
              <Button size="sm" variant="outline" onClick={() => { setTransferForm({ from_account_id: "", to_account_id: "", amount: "", date: today(), note: "" }); setDlg("transfer"); }} data-testid="transfer-button"><ArrowUpRight className="mr-1 h-3.5 w-3.5" /> Bank to Bank Transfer</Button>
              <Button size="sm" className="bg-indigo-600 hover:bg-indigo-700" onClick={() => setDlg("account")} data-testid="add-account-button"><Plus className="mr-1 h-3.5 w-3.5" /> Add Account</Button>
            </div>
          </div>
          <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
            {accounts.filter((a) => (a.type || "cash") === cashTab).map((a) => (
              <Card key={a.id} className={`cursor-pointer border-slate-200 transition-shadow hover:shadow-md ${selAccount?.id === a.id ? "ring-2 ring-indigo-500" : ""}`} data-testid={`account-card-${a.id}`}>
                <CardContent className="p-4" onClick={() => loadAccTxns(a)}>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2 text-sm font-semibold text-slate-900">
                      {a.type === "bank" ? <Landmark className="h-4 w-4 text-indigo-500" /> : <Wallet className="h-4 w-4 text-emerald-500" />}
                      {a.name}
                    </div>
                    <div className="flex items-center gap-1">
                      <Button variant="ghost" size="icon" className="h-7 w-7" onClick={(e) => { e.stopPropagation(); setSelAccount(a); setAdjForm({ balance: a.balance, note: "" }); setDlg("adjust"); }} data-testid={`account-adjust-${a.id}`} aria-label="Adjust balance">
                        <SlidersHorizontal className="h-3.5 w-3.5 text-slate-500" />
                      </Button>
                      <Button variant="ghost" size="icon" className="h-7 w-7" onClick={(e) => { e.stopPropagation(); remove("accounts", a.id); }} data-testid={`account-delete-${a.id}`} aria-label="Delete account"><Trash2 className="h-3.5 w-3.5 text-rose-500" /></Button>
                    </div>
                  </div>
                  <div className="mt-1 text-xs capitalize text-slate-400">{a.type} · opening {inr(a.opening_balance)}</div>
                  <div className="mt-1 font-heading text-lg font-bold" data-testid={`account-balance-${a.id}`}><Money v={a.balance} /></div>
                  <div className="mt-2 flex gap-2">
                    <Button size="sm" variant="outline" className="h-7 flex-1 text-xs" onClick={(e) => { e.stopPropagation(); setSelAccount(a); setCashMoveForm({ kind: "deposit", amount: "", date: today(), note: "" }); setDlg("cash-move"); }} data-testid={`account-deposit-${a.id}`}>Deposit</Button>
                    <Button size="sm" variant="outline" className="h-7 flex-1 text-xs" onClick={(e) => { e.stopPropagation(); setSelAccount(a); setCashMoveForm({ kind: "withdraw", amount: "", date: today(), note: "" }); setDlg("cash-move"); }} data-testid={`account-withdraw-${a.id}`}>Withdraw</Button>
                  </div>
                </CardContent>
              </Card>
            ))}
            {accounts.filter((a) => (a.type || "cash") === cashTab).length === 0 && <p className="text-sm text-slate-500">No {cashTab === "bank" ? "bank accounts" : "cash accounts"} yet — add one to record payments.</p>}
          </div>
          {selAccount && (
            <div className="rounded-lg border border-slate-200 bg-white" data-testid="account-transactions">
              <div className="border-b border-slate-200 px-4 py-3 text-sm font-semibold text-slate-900">Ledger — {selAccount.name}</div>
              <Table>
                <TableHeader><TableRow><SortTh t="account-transactions" k="date" label="Date" testid="sort-account-transactions-date" /><TableHead>Type</TableHead><TableHead>Party / Category</TableHead><TableHead>Description</TableHead><SortTh t="account-transactions" k="amount" label="Amount" className="text-right" testid="sort-account-transactions-amount" /></TableRow></TableHeader>
                <TableBody data-testid="account-transactions-table">
                  {applySortTo("account-transactions", accTxns).map((t) => (
                    <TableRow key={t.id}>
                      <TableCell>{t.date}</TableCell>
                      <TableCell><Badge variant="outline" className="capitalize">{t.kind}</Badge></TableCell>
                      <TableCell className="font-medium">{t.name}</TableCell>
                      <TableCell className="max-w-48 truncate text-slate-500">{t.kind === "receipt" ? `Received from ${t.name}` : t.kind === "payment" ? `Paid to ${t.name}` : t.kind === "deposit" ? "Deposit (self)" : t.kind === "withdraw" ? "Withdrawal (self)" : t.kind === "transfer" ? `Transfer out — ${t.note}` : t.kind === "transfer-in" ? `Transfer in — ${t.note}` : `Expense — ${t.name}`}{t.ref ? ` · ${t.ref}` : ""}{t.note && !t.kind.startsWith("transfer") ? ` · ${t.note}` : ""}</TableCell>
                      <TableCell className={`text-right font-medium ${t.amount >= 0 ? "text-emerald-700" : "text-rose-600"}`}>{t.amount >= 0 ? "+" : ""}{inr(t.amount)}</TableCell>
                    </TableRow>
                  ))}
                  {accTxns.length === 0 && <TableRow><TableCell colSpan={5}><EmptyState compact title="No transactions in this account yet" /></TableCell></TableRow>}
                </TableBody>
              </Table>
            </div>
          )}
        </div>
      )}

      <Dialog open={dlg === "expense"} onOpenChange={(o) => !o && setDlg("")}>
        <DialogContent data-testid="expense-dialog">
          <DialogHeader><DialogTitle>{editExpId ? "Edit Expense" : "Add Expense"}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Date</Label><Input type="date" value={expForm.date} onChange={(e) => setExpForm({ ...expForm, date: e.target.value })} className="mt-1.5" data-testid="expense-date-input" /></div>
              <div><Label>Amount ₹ *</Label><Input type="number" min="0" value={expForm.amount} onChange={(e) => setExpForm({ ...expForm, amount: e.target.value })} className="mt-1.5" data-testid="expense-amount-input" /></div>
            </div>
            <div>
              <Label>Category *</Label>
              <Select
                value={expCatMode === "new" ? "__new" : expForm.category}
                onValueChange={(v) => {
                  if (v === "__new") {
                    setExpCatMode("new"); setInlineCatName(""); setExpForm({ ...expForm, category: "" });
                  } else {
                    setExpCatMode("existing"); setExpForm({ ...expForm, category: v });
                  }
                }}
              >
                <SelectTrigger className="mt-1.5" data-testid="expense-category-select"><SelectValue placeholder="Select category" /></SelectTrigger>
                <SelectContent>
                  {expenseCategoryNames.map((c) => <SelectItem key={c} value={c} data-testid={`expense-category-option-${c.replace(/\W+/g, "-")}`}>{c}</SelectItem>)}
                  <SelectItem value="__new" data-testid="expense-category-option-new">+ Add new category</SelectItem>
                </SelectContent>
              </Select>
              {expCatMode === "new" && (
                <Input value={inlineCatName} onChange={(e) => setInlineCatName(e.target.value)} placeholder="New category name" className="mt-2" data-testid="expense-category-input" />
              )}
            </div>
            <div><Label>Paid From</Label>{accountSelect(expForm.account_id, (v) => setExpForm({ ...expForm, account_id: v }), "expense-account-select")}</div>
            <div><Label>Note</Label><Input value={expForm.note} onChange={(e) => setExpForm({ ...expForm, note: e.target.value })} className="mt-1.5" data-testid="expense-note-input" /></div>
            <Button className="bg-indigo-600 hover:bg-indigo-700" onClick={() => submit("expense")} disabled={saving} data-testid="expense-save-button">{saving && <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />}{editExpId ? "Save Changes" : "Save Expense"}</Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={dlg === "payment"} onOpenChange={(o) => { if (!o) { setDlg(""); setEditPayId(null); } }}>
        <DialogContent data-testid="payment-dialog">
          <DialogHeader><DialogTitle>{editPayId ? (payForm.type === "receipt" ? "Edit Payment-In (Receipt)" : "Edit Payment-Out") : payForm.type === "receipt" ? "Add Payment-In (Receipt)" : "Add Payment-Out"}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            {!editPayId && (
            <label className="flex cursor-pointer items-center gap-2 rounded-md border border-dashed border-indigo-300 bg-indigo-50/40 px-3 py-2 text-sm font-medium text-indigo-700" data-testid="payment-autolink-row">
              <input type="checkbox" className="accent-indigo-600" checked={!!payForm.auto_link} onChange={(e) => setPayForm({ ...payForm, auto_link: e.target.checked, invoice_id: "", purchase_id: "", ref: "" })} data-testid="payment-autolink-toggle" />
              Auto-link — allocate to the party's oldest open bills automatically
            </label>
            )}
            {!payForm.auto_link && (
            <div>
              <Label>{payForm.type === "receipt" ? "Link Sale Invoice" : "Link Purchase Bill"} (optional)</Label>
              {payForm.invoice_id || payForm.purchase_id ? (
                <div className="mt-1.5 flex items-center justify-between rounded-md border border-emerald-200 bg-emerald-50 px-3 py-2" data-testid="payment-link-chip">
                  <span className="text-xs text-emerald-800">Linked to {payForm.ref} — its balance and status update automatically.</span>
                  <Button variant="ghost" size="sm" className="h-6 text-xs" onClick={() => setPayForm({ ...payForm, invoice_id: "", purchase_id: "", ref: "" })} data-testid="payment-link-clear">Clear</Button>
                </div>
              ) : (
                <>
                  <Input value={linkSearch} onChange={(e) => setLinkSearch(e.target.value)} placeholder="Search number / party..." className="mt-1.5" data-testid="payment-link-search" />
                  <div className="mt-1.5 max-h-36 overflow-y-auto rounded-md border border-slate-200" data-testid="payment-link-list">
                    {(payForm.type === "receipt" ? salesRows : purchases)
                      .filter((r) => (r.due ?? 0) > 0.009)
                      .filter((r) => { const q = linkSearch.toLowerCase(); return !q || (r.number || "").toLowerCase().includes(q) || (r.customer || r.vendor || "").toLowerCase().includes(q); })
                      .map((r) => (
                        <button key={r.id} type="button" className="flex w-full items-center justify-between px-3 py-2 text-left text-sm hover:bg-indigo-50" data-testid={`payment-link-${r.id}`}
                          onClick={() => payForm.type === "receipt"
                            ? setPayForm({ ...payForm, invoice_id: r.id, purchase_id: "", ref: r.number, party: r.customer, amount: r.due })
                            : setPayForm({ ...payForm, purchase_id: r.id, invoice_id: "", ref: r.number, party: r.vendor, amount: r.due })}>
                          <span className="font-medium text-slate-800">{r.number} <span className="font-normal text-slate-500">— {r.customer || r.vendor}</span></span>
                          <span className="text-xs font-medium text-rose-600">Due {inr(r.due)}</span>
                        </button>
                      ))}
                    {(payForm.type === "receipt" ? salesRows : purchases).filter((r) => (r.due ?? 0) > 0.009).length === 0 && (
                      <div className="px-3 py-3 text-center text-xs text-slate-400">No open {payForm.type === "receipt" ? "invoices" : "bills"} with balance due.</div>
                    )}
                  </div>
                </>
              )}
            </div>
            )}
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Date</Label><Input type="date" value={payForm.date} onChange={(e) => setPayForm({ ...payForm, date: e.target.value })} className="mt-1.5" data-testid="payment-date-input" /></div>
              <div>
                <Label>Type</Label>
                <Select value={payForm.type} onValueChange={(v) => setPayForm({ ...payForm, type: v })} disabled={!!editPayId}>
                  <SelectTrigger className="mt-1.5" data-testid="payment-type-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="receipt">Payment-In (money in)</SelectItem>
                    <SelectItem value="payment">Payment-Out (money out)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label>Party *</Label>
              <Input value={payForm.party} onChange={(e) => { setPayForm({ ...payForm, party: e.target.value }); setPayPartyQuery(e.target.value); }} placeholder="Search or type party name..." className="mt-1.5" data-testid="payment-party-input" />
              {payPartyQuery && (
                <div className="mt-1 max-h-32 overflow-y-auto rounded-md border border-slate-200" data-testid="payment-party-list">
                  {parties.filter((p) => p.name.toLowerCase().includes(payPartyQuery.toLowerCase())).slice(0, 6).map((p) => (
                    <button key={p.name} type="button" className="flex w-full items-center justify-between px-3 py-1.5 text-left text-sm hover:bg-indigo-50" onClick={() => { setPayForm({ ...payForm, party: p.name }); setPayPartyQuery(""); }} data-testid={`payment-party-${p.name.replace(/\W+/g, "-")}`}>
                      <span className="font-medium">{p.name}</span>
                      <span className={`text-xs ${p.balance > 0 ? "text-emerald-700" : p.balance < 0 ? "text-rose-600" : "text-slate-400"}`}>{p.balance > 0 ? `You'll get ${inr(p.balance)}` : p.balance < 0 ? `You give ${inr(-p.balance)}` : "Settled"}</span>
                    </button>
                  ))}
                  {parties.filter((p) => p.name.toLowerCase().includes(payPartyQuery.toLowerCase())).length === 0 && <div className="px-3 py-2 text-xs text-slate-400">No party matches — it will be saved as typed.</div>}
                </div>
              )}
              {(() => { const pp = parties.find((p) => p.name === payForm.party); return pp ? (
                <div className="mt-1.5 flex flex-wrap gap-x-4 gap-y-1 rounded-md bg-slate-50 px-3 py-2 text-xs" data-testid="payment-party-balance">
                  <span className="text-slate-600">Running balance: <b className={pp.balance > 0 ? "text-emerald-700" : pp.balance < 0 ? "text-rose-600" : ""}>{inr(Math.abs(pp.balance))} {pp.balance > 0 ? "to receive" : pp.balance < 0 ? "to pay" : "settled"}</b></span>
                  {pp.unused > 0 && <span className="text-indigo-700" data-testid="payment-party-unused">Advance in: <b>{inr(pp.unused)}</b> (not linked to any invoice)</span>}
                  {pp.advance_out > 0 && <span className="text-rose-600" data-testid="payment-party-advance-out">Advance paid: <b>{inr(pp.advance_out)}</b> (not linked to any bill)</span>}
                </div>
              ) : null; })()}
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Amount ₹ *</Label><Input type="number" min="0" value={payForm.amount} onChange={(e) => setPayForm({ ...payForm, amount: e.target.value })} className="mt-1.5" data-testid="payment-amount-input" /></div>
              <div>
                <Label>Account *</Label>
                {accountSelect(payForm.account_id, (v) => setPayForm({ ...payForm, account_id: v }), "payment-account-select")}
                <button type="button" className="mt-1 text-xs font-medium text-indigo-600 hover:underline" data-testid="payment-add-account-inline"
                  onClick={async () => {
                    const name = window.prompt("New account name (e.g. Cash in Hand / HDFC Bank)");
                    if (!name?.trim()) return;
                    try {
                      const { data } = await API.post("/accounting/accounts", { name: name.trim(), type: "bank", opening_balance: 0 });
                      setAccounts([...accounts, data]);
                      setPayForm({ ...payForm, account_id: data.id });
                      toast.success("Account added");
                    } catch (e) { toast.error(e.response?.data?.detail || "Could not add account"); }
                  }}>+ Add new account</button>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Description</Label><Input value={payForm.note} onChange={(e) => setPayForm({ ...payForm, note: e.target.value })} className="mt-1.5" data-testid="payment-note-input" /></div>
              <div>
                <Label>Attach Image (receipt/photo)</Label>
                {payForm.image_url ? (
                  <div className="mt-1.5 flex items-center justify-between rounded-md border border-slate-200 px-3 py-2" data-testid="payment-image-chip">
                    <a href={payForm.image_url} target="_blank" rel="noreferrer" className="text-xs text-indigo-700 hover:underline" data-testid="payment-image-view">View attachment</a>
                    <Button variant="ghost" size="icon" className="h-6 w-6" onClick={() => setPayForm({ ...payForm, image_url: "" })} data-testid="payment-image-remove" aria-label="Remove image"><Trash2 className="h-3.5 w-3.5 text-rose-500" /></Button>
                  </div>
                ) : (
                  <label className="mt-1.5 flex cursor-pointer items-center justify-center rounded-md border border-dashed border-slate-300 px-3 py-2 text-xs text-slate-500 hover:bg-slate-50" data-testid="payment-image-upload">
                    <input type="file" accept="image/*" className="hidden" onChange={async (e) => {
                      const f = e.target.files?.[0];
                      if (!f) return;
                      const fd = new FormData();
                      fd.append("file", f);
                      try {
                        const { data } = await API.post("/upload", fd);
                        setPayForm({ ...payForm, image_url: data.url });
                        toast.success("Image attached");
                      } catch { toast.error("Upload failed"); }
                    }} />
                    Upload image
                  </label>
                )}
              </div>
            </div>
            <Button className="bg-indigo-600 hover:bg-indigo-700" onClick={() => submit("payment")} disabled={saving} data-testid="payment-save-button">{saving && <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />}{editPayId ? "Save Changes" : "Save"}</Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={dlg === "import-parties"} onOpenChange={(o) => !o && setDlg("")}>
        <DialogContent data-testid="import-parties-dialog">
          <DialogHeader><DialogTitle>Import Phone Contacts as Parties</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <p className="text-xs text-slate-500">Paste one contact per line: <b>Name, Phone</b> (phone optional). Duplicates are skipped.</p>
            <Textarea rows={6} value={importText} onChange={(e) => setImportText(e.target.value)} placeholder={"Ramesh Traders, 9876543210\nSuresh Kumar, 9123456780"} data-testid="import-parties-text" />
            <Button className="bg-indigo-600 hover:bg-indigo-700" disabled={importing} data-testid="import-parties-submit"
              onClick={async () => {
                const rows = importText.split("\n").map((ln) => { const [name, phone] = ln.split(/[,\t]/); return { name: (name || "").trim(), phone: (phone || "").trim() }; }).filter((r) => r.name);
                if (!rows.length) return toast.error("Paste at least one contact");
                setImporting(true);
                try {
                  const { data } = await API.post("/accounting/parties/import", { rows });
                  toast.success(`Imported ${data.created} parties (${data.skipped} skipped)`);
                  setImportText(""); setDlg(""); load();
                } catch (e) { toast.error(e.response?.data?.detail || "Import failed"); } finally { setImporting(false); }
              }}>
              {importing && <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />} Import Contacts
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={dlg === "cash-move"} onOpenChange={(o) => !o && setDlg("")}>
        <DialogContent data-testid="cash-move-dialog">
          <DialogHeader><DialogTitle className="capitalize">{cashMoveForm.kind} — {selAccount?.name}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Date</Label><Input type="date" value={cashMoveForm.date} onChange={(e) => setCashMoveForm({ ...cashMoveForm, date: e.target.value })} className="mt-1.5" data-testid="cash-move-date" /></div>
              <div><Label>Amount ₹ *</Label><Input type="number" min="0" value={cashMoveForm.amount} onChange={(e) => setCashMoveForm({ ...cashMoveForm, amount: e.target.value })} className="mt-1.5" data-testid="cash-move-amount" /></div>
            </div>
            <div><Label>Note</Label><Input value={cashMoveForm.note} onChange={(e) => setCashMoveForm({ ...cashMoveForm, note: e.target.value })} placeholder={cashMoveForm.kind === "deposit" ? "Cash deposited to bank" : "Cash withdrawn for petty use"} className="mt-1.5" data-testid="cash-move-note" /></div>
            <Button className="bg-indigo-600 hover:bg-indigo-700" disabled={saving} data-testid="cash-move-save"
              onClick={async () => {
                if (!selAccount || !(Number(cashMoveForm.amount) > 0)) return toast.error("Enter a valid amount");
                setSaving(true);
                try {
                  await API.post(`/accounting/accounts/${selAccount.id}/cash-move`, { ...cashMoveForm, amount: Number(cashMoveForm.amount) });
                  toast.success(`${cashMoveForm.kind === "deposit" ? "Deposited" : "Withdrawn"} ${inr(Number(cashMoveForm.amount))}`);
                  setDlg(""); loadAccTxns(selAccount); load();
                } catch (e) { toast.error(e.response?.data?.detail || "Failed"); } finally { setSaving(false); }
              }}>{saving && <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />}Save {cashMoveForm.kind === "deposit" ? "Deposit" : "Withdrawal"}</Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={dlg === "transfer"} onOpenChange={(o) => !o && setDlg("")}>
        <DialogContent data-testid="transfer-dialog">
          <DialogHeader><DialogTitle>Bank to Bank Transfer</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div><Label>From Account *</Label>{accountSelect(transferForm.from_account_id, (v) => setTransferForm({ ...transferForm, from_account_id: v }), "transfer-from-select")}</div>
              <div><Label>To Account *</Label>{accountSelect(transferForm.to_account_id, (v) => setTransferForm({ ...transferForm, to_account_id: v }), "transfer-to-select")}</div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Date</Label><Input type="date" value={transferForm.date} onChange={(e) => setTransferForm({ ...transferForm, date: e.target.value })} className="mt-1.5" data-testid="transfer-date" /></div>
              <div><Label>Amount ₹ *</Label><Input type="number" min="0" value={transferForm.amount} onChange={(e) => setTransferForm({ ...transferForm, amount: e.target.value })} className="mt-1.5" data-testid="transfer-amount" /></div>
            </div>
            <div><Label>Note</Label><Input value={transferForm.note} onChange={(e) => setTransferForm({ ...transferForm, note: e.target.value })} placeholder="Optional — defaults to 'From → To'" className="mt-1.5" data-testid="transfer-note" /></div>
            <Button className="bg-indigo-600 hover:bg-indigo-700" disabled={saving} data-testid="transfer-save"
              onClick={async () => {
                if (!transferForm.from_account_id || !transferForm.to_account_id) return toast.error("Select both accounts");
                if (transferForm.from_account_id === transferForm.to_account_id) return toast.error("From and To accounts must differ");
                if (!(Number(transferForm.amount) > 0)) return toast.error("Enter a valid amount");
                setSaving(true);
                try {
                  await API.post("/accounting/accounts/transfer", { ...transferForm, amount: Number(transferForm.amount) });
                  toast.success("Transfer recorded");
                  setDlg(""); if (selAccount) loadAccTxns(selAccount); load();
                } catch (e) { toast.error(e.response?.data?.detail || "Transfer failed"); } finally { setSaving(false); }
              }}>{saving && <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />}Transfer</Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={dlg === "account"} onOpenChange={(o) => !o && setDlg("")}>
        <DialogContent data-testid="account-dialog">
          <DialogHeader><DialogTitle>Add Cash / Bank Account</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div><Label>Name *</Label><Input value={accForm.name} onChange={(e) => setAccForm({ ...accForm, name: e.target.value })} placeholder="Cash in hand / HDFC Bank" className="mt-1.5" data-testid="account-name-input" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Type</Label>
                <Select value={accForm.type} onValueChange={(v) => setAccForm({ ...accForm, type: v })}>
                  <SelectTrigger className="mt-1.5" data-testid="account-type-select"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="cash">Cash</SelectItem><SelectItem value="bank">Bank</SelectItem></SelectContent>
                </Select>
              </div>
              <div><Label>Opening Balance ₹</Label><Input type="number" value={accForm.opening_balance} onChange={(e) => setAccForm({ ...accForm, opening_balance: e.target.value })} className="mt-1.5" data-testid="account-opening-input" /></div>
            </div>
            <Button className="bg-indigo-600 hover:bg-indigo-700" onClick={() => submit("account")} disabled={saving} data-testid="account-save-button">{saving && <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />}Save Account</Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={dlg === "adjust"} onOpenChange={(o) => !o && setDlg("")}>
        <DialogContent data-testid="adjust-account-dialog">
          <DialogHeader><DialogTitle>Adjust Balance — {selAccount?.name}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <p className="text-xs text-slate-500">Book balance: <b>{selAccount ? inr(selAccount.balance) : ""}</b>. Enter the actual balance — the difference is recorded as an adjustment entry.</p>
            <div><Label>Actual Balance ₹</Label><Input type="number" value={adjForm.balance} onChange={(e) => setAdjForm({ ...adjForm, balance: e.target.value })} className="mt-1.5" data-testid="adjust-balance-input" /></div>
            <div><Label>Note</Label><Input value={adjForm.note} onChange={(e) => setAdjForm({ ...adjForm, note: e.target.value })} placeholder="Cash count mismatch / correction" className="mt-1.5" data-testid="adjust-note-input" /></div>
            <Button className="bg-indigo-600 hover:bg-indigo-700" onClick={async () => { if (saving) return; setSaving(true); try { await API.post(`/accounting/accounts/${selAccount.id}/adjust`, { balance: Number(adjForm.balance), note: adjForm.note }); toast.success("Balance adjusted"); setDlg(""); loadAccTxns(selAccount); load(); } catch (e) { toast.error(e.response?.data?.detail || "Adjust failed"); } finally { setSaving(false); } }} disabled={saving} data-testid="adjust-save-button">{saving && <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />}Save Adjustment</Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={dlg === "sale-return"} onOpenChange={(o) => !o && setDlg("")}>
        <DialogContent data-testid="sale-return-dialog">
          <DialogHeader><DialogTitle>New Sale Return</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Invoice *</Label>
              <Select value={srForm.invoice_id} onValueChange={pickSrInvoice}>
                <SelectTrigger className="mt-1.5" data-testid="sale-return-invoice-select"><SelectValue placeholder="Select invoice" /></SelectTrigger>
                <SelectContent>
                  {salesRows.map((r) => <SelectItem key={r.id} value={r.id}>{r.number} — {r.customer} ({inr(r.total)})</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            {srItems.length > 0 && (
              <div className="space-y-2">
                <Label>Items to return</Label>
                {srItems.map((i) => (
                  <div key={i.product_id} className="flex items-center justify-between gap-3 rounded-md border border-slate-200 p-2.5">
                    <div className="text-sm"><div className="font-medium text-slate-800">{i.name}</div><div className="text-xs text-slate-400">sold: {i.qty}</div></div>
                    <Input type="number" min="0" max={i.qty} className="w-24" value={srForm.qtys[i.product_id] ?? 0} onChange={(e) => setSrForm((f) => ({ ...f, qtys: { ...f.qtys, [i.product_id]: e.target.value } }))} data-testid={`sale-return-qty-${i.product_id}`} />
                  </div>
                ))}
              </div>
            )}
            <div><Label>Reason</Label><Input value={srForm.reason} onChange={(e) => setSrForm({ ...srForm, reason: e.target.value })} placeholder="Damaged / wrong item / order cancelled" className="mt-1.5" data-testid="sale-return-reason-input" /></div>
            <Button className="bg-indigo-600 hover:bg-indigo-700" onClick={() => submit("sale-return")} disabled={saving} data-testid="sale-return-save-button">{saving && <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />}Save Return</Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={dlg === "purchase"} onOpenChange={(o) => !o && setDlg("")}>
        <DialogContent className="max-w-2xl" data-testid="purchase-dialog">
          <DialogHeader><DialogTitle>{editPurId ? "Edit Purchase" : "New Purchase (Direct Vendor)"}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Vendor *</Label><Input value={purForm.vendor} onChange={(e) => setPurForm({ ...purForm, vendor: e.target.value })} placeholder="Company 1 (B2C) / outside vendor" className="mt-1.5" data-testid="purchase-vendor-input" /></div>
              <div><Label>Date</Label><Input type="date" value={purForm.date} onChange={(e) => setPurForm({ ...purForm, date: e.target.value })} className="mt-1.5" data-testid="purchase-date-input" /></div>
            </div>
            <div>
              <div className="mb-2 flex items-center justify-between">
                <Label>Items</Label>
                <Button size="sm" variant="outline" onClick={() => setPurForm((f) => ({ ...f, items: [...f.items, { product_id: "", name: "", qty: 1, rate: 0 }] }))} data-testid="add-purchase-item-button"><Plus className="mr-1 h-3.5 w-3.5" /> Add item</Button>
              </div>
              <div className="max-h-56 space-y-2 overflow-y-auto">
                {purForm.items.map((it, i) => (
                  <div key={i} className="flex items-end gap-2 rounded-md border border-slate-200 p-2.5">
                    <div className="flex-1">
                      <div className="mb-0.5 text-[10px] font-semibold uppercase tracking-wide text-slate-400">Product (optional)</div>
                      <Select value={it.product_id || "custom"} onValueChange={(v) => setPurForm((f) => ({ ...f, items: f.items.map((x, idx) => idx === i ? { ...x, product_id: v === "custom" ? "" : v, name: v === "custom" ? x.name : (products.find((p) => p.id === v)?.name || x.name) } : x) }))}>
                        <SelectTrigger data-testid={`purchase-item-product-${i}`}><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="custom">Custom item</SelectItem>
                          {products.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                    {!it.product_id && (
                      <div className="flex-1">
                        <div className="mb-0.5 text-[10px] font-semibold uppercase tracking-wide text-slate-400">Item name</div>
                        <Input value={it.name} onChange={(e) => setPurForm((f) => ({ ...f, items: f.items.map((x, idx) => idx === i ? { ...x, name: e.target.value } : x) }))} data-testid={`purchase-item-name-${i}`} />
                      </div>
                    )}
                    <div className="w-20">
                      <div className="mb-0.5 text-[10px] font-semibold uppercase tracking-wide text-slate-400">Qty</div>
                      <Input type="number" min="1" value={it.qty} onChange={(e) => setPurForm((f) => ({ ...f, items: f.items.map((x, idx) => idx === i ? { ...x, qty: e.target.value } : x) }))} data-testid={`purchase-item-qty-${i}`} />
                    </div>
                    <div className="w-24">
                      <div className="mb-0.5 text-[10px] font-semibold uppercase tracking-wide text-slate-400">Rate ₹</div>
                      <Input type="number" min="0" value={it.rate} onChange={(e) => setPurForm((f) => ({ ...f, items: f.items.map((x, idx) => idx === i ? { ...x, rate: e.target.value } : x) }))} data-testid={`purchase-item-rate-${i}`} />
                    </div>
                    <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => setPurForm((f) => ({ ...f, items: f.items.filter((_, idx) => idx !== i) }))} aria-label="Remove"><Trash2 className="h-3.5 w-3.5 text-rose-500" /></Button>
                  </div>
                ))}
              </div>
              <div className="mt-2 text-right text-sm font-semibold text-slate-800">Total: {inr(purForm.items.reduce((a, i) => a + (Number(i.qty) || 0) * (Number(i.rate) || 0), 0))}</div>
            </div>
            {!editPurId && (
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Paid now ₹ (optional)</Label><Input type="number" min="0" value={purForm.paid_amount} onChange={(e) => setPurForm({ ...purForm, paid_amount: e.target.value })} className="mt-1.5" data-testid="purchase-paid-input" /></div>
                <div><Label>Paid From</Label>{accountSelect(purForm.account_id, (v) => setPurForm({ ...purForm, account_id: v }), "purchase-account-select")}</div>
              </div>
            )}
            <div><Label>Note</Label><Input value={purForm.note} onChange={(e) => setPurForm({ ...purForm, note: e.target.value })} className="mt-1.5" data-testid="purchase-note-input" /></div>
            <Button className="bg-indigo-600 hover:bg-indigo-700" onClick={() => submit("purchase")} disabled={saving} data-testid="purchase-save-button">{saving && <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />}{editPurId ? "Save Changes" : "Create Purchase Bill"}</Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={dlg === "purchase-return"} onOpenChange={(o) => !o && setDlg("")}>
        <DialogContent data-testid="purchase-return-dialog">
          <DialogHeader><DialogTitle>Purchase Return — {prForm.purchase?.number}</DialogTitle></DialogHeader>
          <p className="text-xs text-slate-500">Stock moves out of the B2B warehouse{prForm.purchase?.challan_id ? " and back to Company 1" : ""}.</p>
          <div className="space-y-2">
            {prForm.purchase?.items?.filter((i) => i.product_id).map((i) => (
              <div key={i.product_id} className="flex items-center justify-between gap-3 rounded-md border border-slate-200 p-2.5">
                <div className="text-sm"><div className="font-medium text-slate-800">{i.name}</div><div className="text-xs text-slate-400">purchased: {i.qty}</div></div>
                <Input type="number" min="0" max={i.qty} className="w-24" value={prForm.qtys[i.product_id] ?? 0} onChange={(e) => setPrForm((f) => ({ ...f, qtys: { ...f.qtys, [i.product_id]: e.target.value } }))} data-testid={`purchase-return-qty-${i.product_id}`} />
              </div>
            ))}
          </div>
          <div><Label>Reason</Label><Input value={prForm.reason} onChange={(e) => setPrForm({ ...prForm, reason: e.target.value })} placeholder="Order cancelled / damaged / excess" className="mt-1.5" data-testid="purchase-return-reason-input" /></div>
          <Button className="bg-rose-600 hover:bg-rose-700" onClick={() => submit("purchase-return")} disabled={saving} data-testid="purchase-return-save-button">{saving && <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />}Save Purchase Return</Button>
        </DialogContent>
      </Dialog>

      <Dialog open={dlg === "party"} onOpenChange={(o) => !o && setDlg("")}>
        <DialogContent data-testid="party-dialog">
          <DialogHeader><DialogTitle>{editPartyId ? "Edit Party" : "Add New Party"}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div><Label>Name *</Label><Input value={partyForm.name} onChange={(e) => setPartyForm({ ...partyForm, name: e.target.value })} placeholder="Vendor / customer name" className="mt-1.5" data-testid="party-name-input" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Type</Label>
                <Select value={partyForm.type} onValueChange={(v) => setPartyForm({ ...partyForm, type: v })}>
                  <SelectTrigger className="mt-1.5" data-testid="party-type-select"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="vendor">Vendor</SelectItem><SelectItem value="customer">Customer</SelectItem><SelectItem value="both">Both</SelectItem></SelectContent>
                </Select>
              </div>
              <div><Label>Phone</Label><Input value={partyForm.phone} onChange={(e) => setPartyForm({ ...partyForm, phone: e.target.value })} className="mt-1.5" data-testid="party-phone-input" /></div>
            </div>
            <div><Label>Opening Balance ₹ (+ they owe you / − you owe them)</Label><Input type="number" value={partyForm.opening_balance} onChange={(e) => setPartyForm({ ...partyForm, opening_balance: e.target.value })} className="mt-1.5" data-testid="party-opening-input" /></div>
            <Button className="bg-indigo-600 hover:bg-indigo-700" onClick={() => submit("party")} disabled={saving} data-testid="party-save-button">{saving && <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />}Save Party</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
